import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { collection, getDocs, updateDoc, doc, addDoc, deleteDoc, getDoc, runTransaction, arrayUnion, query, where, limit, serverTimestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../firebaseConfig";
import JobCompletionInvoiceModal from "../components/JobCompletionInvoiceModal";
import JobHistoryPopup from "../components/JobHistoryPopup";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import moment from "moment";
import * as XLSX from "xlsx";
import { useLocation, useNavigate } from "react-router-dom";
import "./global.css";
import { addJobTotal } from "../utils/addJobTotal";
import { getOccurrencesForJob } from "../utils/getOccurrencesForJob";
import {
  refreshKeepingView,
  restoreScrollAfterRefresh,
  getCompletionOccurrenceKey,
  makeOccurrenceDateMatcher,
} from "../utils/recurringOccurrenceUtils";
import { applyOccurrenceOverrideToRenderedJob, saveRecurringJobEdit } from "../utils/recurringEditUtils";



const LoadingSpinner = ({ label = "Saving" }) => (
  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
    <style>{`@keyframes jmc-note-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        border: "2px solid rgba(255,255,255,0.65)",
        borderTopColor: "#111827",
        borderRadius: "50%",
        display: "inline-block",
        animation: "jmc-note-spin 0.8s linear infinite",
      }}
    />
    {label}
  </span>
);

const SCHED_COUNTER_REF = doc(db, "counters", "scheduling_wo");

/** Peek at the *next* WO number without incrementing (for the form display). */
const peekNextWoNumber = async () => {
  const snap = await getDoc(SCHED_COUNTER_REF);
  // default lastNumber = 2000 so the first usable is 2001
  const last = snap.exists() ? Number(snap.data()?.lastNumber) : 2000;
  return Number.isFinite(last) ? last + 1 : 2001;
};

/** Atomically get + increment the WO number for saving a job. */
const getNextWoNumber = async () => {
  const next = await runTransaction(db, async (tx) => {
    const snap = await tx.get(SCHED_COUNTER_REF);
    let last = 2000;

    if (!snap.exists()) {
      // initialize so the *first* next is 2001
      tx.set(SCHED_COUNTER_REF, { lastNumber: 2000 });
    } else {
      const raw = Number(snap.data()?.lastNumber);
      last = Number.isFinite(raw) ? raw : 2000;
    }

    const newLast = last + 1; // e.g., 2001 on first run
    tx.update(SCHED_COUNTER_REF, { lastNumber: newLast });
    return newLast;
  });

  return next; // numeric, like 2001, 2002, ...
};

const normalizeText = (value = "") =>
  value
    .toString()
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const cleanTemplateText = (value = "") =>
  String(value || "")
    .replace(/\(\(\s*[bir-]\s*\)\)/gi, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const getServiceCode = (value = "") =>
  String(value || "").trim().split(/\s+-\s+|\s+/)[0].toUpperCase();

const getServiceScope = (serviceType = "") => {
  const value = String(serviceType || "").trim();
  if (!value) return "";

  const separatorIndex = value.indexOf(" - ");
  return separatorIndex >= 0
    ? value.slice(separatorIndex + 3).trim()
    : value;
};


const readPropertyPricing = (property = {}, serviceType = "") => {
  const code = getServiceCode(serviceType);
  const overrides = property?.servicePricingOverrides || {};
  const override = overrides[code] || overrides[serviceType] || {};
  const legacy = property?.serviceTypeTotals || property?.serviceTotals || {};
  return {
    amount: override.amount ?? override.total ?? legacy[code] ?? legacy[serviceType] ?? "",
    initialAmount: override.initialAmount ?? "",
    feeSchedule: override.feeSchedule || "",
  };
};
const resolveServicePricing = (property = {}, template = {}, serviceType = "") => {
  const override = readPropertyPricing(property, serviceType);
  return {
    amount: override.amount !== "" && override.amount != null ? override.amount : (template?.total ?? ""),
    initialAmount: override.initialAmount !== "" && override.initialAmount != null ? override.initialAmount : (template?.initialAmount ?? ""),
    feeSchedule: override.feeSchedule || template?.feeSchedule || "Monthly",
  };
};

const getPropertyServiceTotal = (property = {}, serviceType = "") => {
  const code = getServiceCode(serviceType);
  const map = property.serviceTypeTotals || property.serviceTotals || {};
  const value = map[code] ?? map[serviceType];
  return value === "" || value == null ? "" : value;
};

const tokenize = (value = "") => {
  const stopWords = new Set([
    "the", "a", "an", "and", "or", "for", "to", "of", "in", "on", "at",
    "with", "by", "from", "job", "work", "service"
  ]);

  return normalizeText(value)
    .split(" ")
    .filter(Boolean)
    .filter(word => !stopWords.has(word));
};

const jaccardSimilarity = (a = "", b = "") => {
  const setA = new Set(tokenize(a));
  const setB = new Set(tokenize(b));

  if (setA.size === 0 && setB.size === 0) return 1;
  if (setA.size === 0 || setB.size === 0) return 0;

  const intersection = [...setA].filter(x => setB.has(x)).length;
  const union = new Set([...setA, ...setB]).size;

  return union === 0 ? 0 : intersection / union;
};

const normalizeCompanyUse = (job) =>
  (job.companyUse === "Other"
    ? (job.companyUseOther || "")
    : (job.companyUse || "")
  ).trim();

const buildExactDuplicateKey = (job) => {
  return [
    normalizeText(job.siteName),
    normalizeText(job.serviceType),
    normalizeText(job.startDate),
    normalizeText(normalizeCompanyUse(job)),
    normalizeText(job.clientWorkOrder),
    normalizeText(job.scope),
  ].join("|");
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return moment(a).format("YYYY-MM-DD") === moment(b).format("YYYY-MM-DD");
};

const findExactDuplicate = (draftJob, existingJobs = []) => {
  const draftKey = buildExactDuplicateKey(draftJob);

  return existingJobs.find(existing => {
    if (existing.status === "Deleted") return false;

    const existingLikeDraft = {
      ...existing,
      companyUseOther: "",
    };

    return buildExactDuplicateKey(existingLikeDraft) === draftKey;
  });
};

const findPossibleDuplicates = (draftJob, existingJobs = []) => {
  const draftScope = draftJob.scope || "";
  const draftDescription = draftJob.jobDescription || "";
  const draftCombined = `${draftScope} ${draftDescription}`.trim();

  return existingJobs
    .filter(existing => existing.status !== "Deleted")
    .map(existing => {
      const existingCombined = `${existing.scope || ""} ${existing.jobDescription || ""}`.trim();

      const scopeScore = jaccardSimilarity(draftScope, existing.scope || "");
      const combinedScore = jaccardSimilarity(draftCombined, existingCombined);

      let score = combinedScore;

      if (normalizeText(existing.siteName) === normalizeText(draftJob.siteName)) {
        score += 0.20;
      }

      if (normalizeText(existing.serviceType) === normalizeText(draftJob.serviceType)) {
        score += 0.15;
      }

      if (isSameDay(existing.startDate, draftJob.startDate)) {
        score += 0.15;
      }

      if (
        normalizeText(existing.clientWorkOrder || "") &&
        normalizeText(existing.clientWorkOrder || "") === normalizeText(draftJob.clientWorkOrder || "")
      ) {
        score += 0.30;
      }

      return {
        ...existing,
        duplicateScore: Math.min(score, 1),
      };
    })
    .filter(item => item.duplicateScore >= 0.55)
    .sort((a, b) => b.duplicateScore - a.duplicateScore)
    .slice(0, 5);
};


const normalizeWOValue = (value = "") => String(value || "").trim();

const getWOStemRoot = (wo = "") => {
  const text = normalizeWOValue(wo);
  if (!text) return "";

  // 3345-2 -> 3345
  const numericBranch = text.match(/^(\d+)-\d+$/);
  if (numericBranch) return numericBranch[1];

  // PJMC-10021-2 -> PJMC-10021, but PJMC-10021 stays PJMC-10021
  const prefixedBranch = text.match(/^([A-Za-z]+-\d+)-\d+$/);
  if (prefixedBranch) return prefixedBranch[1];

  return text;
};

const getWOStemBranchNumber = (wo = "", stem = "") => {
  const text = normalizeWOValue(wo);
  const root = getWOStemRoot(stem || text);

  if (!text || !root) return null;
  if (text === root) return 1;

  const match = text.match(new RegExp(`^${root.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}-(\\d+)$`));
  return match ? Number(match[1]) : null;
};

const getNextAvailableStemWOFromList = (jobsList = [], selectedStem = "") => {
  const root = getWOStemRoot(selectedStem);
  if (!root) return "";

  const usedBranchNumbers = new Set([1]);

  jobsList.forEach((item) => {
    const branchNumber = getWOStemBranchNumber(item?.wo, root);
    if (Number.isFinite(branchNumber)) usedBranchNumbers.add(branchNumber);
  });

  let nextBranch = 2;
  while (usedBranchNumbers.has(nextBranch)) {
    nextBranch += 1;
  }

  return `${root}-${nextBranch}`;
};

const getNextAvailableStemWO = async (selectedStem = "") => {
  const root = getWOStemRoot(selectedStem);
  if (!root) return "";

  const snap = await getDocs(collection(db, "jobs"));
  const allJobs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  return getNextAvailableStemWOFromList(allJobs, root);
};

const buildWOStemOptions = (jobsList = []) => {
  const map = new Map();

  jobsList.forEach((item) => {
    const root = getWOStemRoot(item?.wo);
    if (!root) return;

    const existing = map.get(root) || {
      root,
      siteName: item.siteName || item.propertyName || "",
      scope: item.scope || item.jobDescription || "",
      count: 0,
    };

    existing.count += 1;

    if (!existing.siteName && (item.siteName || item.propertyName)) {
      existing.siteName = item.siteName || item.propertyName;
    }

    if (!existing.scope && (item.scope || item.jobDescription)) {
      existing.scope = item.scope || item.jobDescription;
    }

    map.set(root, existing);
  });

  return Array.from(map.values()).sort((a, b) =>
    String(a.root).localeCompare(String(b.root), undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );
};


const DEFAULT_JOB_SKILL_KEYWORDS = [
  { skill: "Drywall", keywords: ["drywall", "sheetrock", "gypsum", "patch wall", "wall repair"] },
  { skill: "Painting", keywords: ["paint", "painting", "prime", "primer", "stain"] },
  { skill: "Framing", keywords: ["framing", "frame", "stud", "wall build", "rough carpentry"] },
  { skill: "Carpentry", keywords: ["carpentry", "wood", "trim", "cabinet", "shelving"] },
  { skill: "Trim", keywords: ["trim", "baseboard", "casing", "crown", "molding"] },
  { skill: "Flooring", keywords: ["floor", "flooring", "lvp", "vinyl plank", "hardwood", "carpet"] },
  { skill: "Tile", keywords: ["tile", "grout", "backsplash", "shower pan"] },
  { skill: "Concrete", keywords: ["concrete", "sidewalk", "curb", "slab"] },
  { skill: "Masonry", keywords: ["brick", "block", "masonry", "stone", "cmu"] },
  { skill: "Electrical", keywords: ["electric", "electrical", "outlet", "switch", "breaker", "light fixture", "gfci"] },
  { skill: "Plumbing", keywords: ["plumbing", "pipe", "toilet", "sink", "faucet", "hose bib", "water heater"] },
  { skill: "HVAC", keywords: ["hvac", "air conditioning", "ac ", "heat pump", "furnace"] },
  { skill: "Roofing", keywords: ["roof", "shingle", "flashing", "leak"] },
  { skill: "Siding", keywords: ["siding", "vinyl siding", "fiber cement"] },
  { skill: "Windows", keywords: ["window", "windows"] },
  { skill: "Doors", keywords: ["door", "doors", "lockset", "latch"] },
  { skill: "Decking", keywords: ["deck", "decking", "railing", "joist"] },
  { skill: "Fencing", keywords: ["fence", "fencing", "gate", "picket", "post"] },
  { skill: "Retaining Walls", keywords: ["retaining wall", "timber wall", "wall tier"] },
  { skill: "Landscaping", keywords: ["landscape", "mulch", "bush", "tree", "plant"] },
  { skill: "Drainage", keywords: ["drain", "drainage", "downspout", "french drain"] },
  { skill: "Waterproofing", keywords: ["waterproof", "waterproofing", "foundation coating"] },
  { skill: "Cleaning", keywords: ["clean", "cleaning", "janitorial"] },
  { skill: "Bulk Trash", keywords: ["bulk trash", "trash out", "debris removal", "haul"] },
  { skill: "Inspection", keywords: ["inspection", "inspect"] },
  { skill: "Maintenance", keywords: ["maintenance", "repair", "punch list"] },
  { skill: "Demo", keywords: ["demo", "demolition", "remove"] },
  { skill: "Excavation", keywords: ["excavate", "excavation", "dig", "trench"] },
  { skill: "Welding", keywords: ["weld", "welding"] },
];

const SCHEDULE_DAY_HOURS = 8;
const SCHEDULE_TRAVEL_BUFFER_HOURS = 0.75;
const SCHEDULE_SEARCH_MONTHS = 6;

const scheduleDateKey = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");

const addWeekdays = (startKey, daysToAdd) => {
  const cursor = moment(startKey, "YYYY-MM-DD");
  let added = 0;

  while (added < daysToAdd) {
    cursor.add(1, "day");
    if (cursor.day() !== 0 && cursor.day() !== 6) added += 1;
  }

  return cursor.format("YYYY-MM-DD");
};

const getWeekdaySpan = (startKey, daysNeeded) => {
  const out = [];
  const cursor = moment(startKey, "YYYY-MM-DD");

  while (out.length < daysNeeded) {
    if (cursor.day() !== 0 && cursor.day() !== 6) {
      out.push(cursor.format("YYYY-MM-DD"));
    }
    cursor.add(1, "day");
  }

  return out;
};

const hoursFromScheduleWindow = (startTime, endTime) => {
  if (!startTime && !endTime) return SCHEDULE_DAY_HOURS;

  const parseClock = (value) => {
    const clean = String(value || "").trim();

    if (/^\d{1,2}:\d{2}$/.test(clean)) {
      const [h, m] = clean.split(":").map(Number);
      return h + m / 60;
    }

    const match = clean.match(/(\d{1,2})\s*(AM|PM)\s*-\s*(\d{1,2})\s*(AM|PM)/i);
    if (match) {
      const to24 = (hour, suffix) => {
        let h = Number(hour);
        const s = String(suffix).toUpperCase();
        if (s === "PM" && h !== 12) h += 12;
        if (s === "AM" && h === 12) h = 0;
        return h;
      };

      return {
        start: to24(match[1], match[2]),
        end: to24(match[3], match[4]),
      };
    }

    return null;
  };

  const startParsed = parseClock(startTime);
  const endParsed = parseClock(endTime);

  if (startParsed && typeof startParsed === "object" && "start" in startParsed) {
    return Math.max(1, startParsed.end - startParsed.start);
  }

  if (typeof startParsed === "number" && typeof endParsed === "number") {
    return Math.max(1, endParsed - startParsed);
  }

  return SCHEDULE_DAY_HOURS;
};

const getStaffDisplayName = (user = {}) =>
  `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
  user.name ||
  user.displayName ||
  user.email ||
  "";

const normalizeScheduleSkill = (value = "") =>
  String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const staffHasAllSkill = (staffMember = {}) => {
  const role = normalizeScheduleSkill(staffMember.role);
  const position = normalizeScheduleSkill(staffMember.position);
  const skills = Array.isArray(staffMember.skills) ? staffMember.skills : [];

  return (
    role === "supervisor" ||
    position.includes("supervisor") ||
    skills.some((skill) => ["all", "supervisor"].includes(normalizeScheduleSkill(skill)))
  );
};

const inferRequiredSkillsForJob = (job = {}) => {
  const text = normalizeScheduleSkill(
    `${job.serviceType || ""} ${job.scope || ""} ${job.jobDescription || ""}`
  );

  const matches = DEFAULT_JOB_SKILL_KEYWORDS.filter((item) =>
    item.keywords.some((keyword) => text.includes(normalizeScheduleSkill(keyword)))
  ).map((item) => item.skill);

  if (matches.length) return Array.from(new Set(matches));

  if (job.serviceType && !["Proposal", "Meeting", "Action Items"].includes(job.serviceType)) {
    return [job.serviceType];
  }

  return ["Maintenance"];
};

const staffMatchesRequiredSkills = (staffMember = {}, requiredSkills = []) => {
  if (staffHasAllSkill(staffMember)) return true;

  const staffSkills = Array.isArray(staffMember.skills) ? staffMember.skills : [];
  const normalizedStaffSkills = staffSkills.map(normalizeScheduleSkill);
  const normalizedRequired = requiredSkills.map(normalizeScheduleSkill);

  return normalizedRequired.some((required) =>
    normalizedStaffSkills.some((skill) => skill === required || skill.includes(required) || required.includes(skill))
  );
};

const getJobAssignedTechNames = (job = {}) => {
  if (Array.isArray(job.techAssign)) return job.techAssign.filter(Boolean);
  return job.techAssign ? [job.techAssign] : [];
};

const buildTechLoadMap = (jobs = [], excludeJobId = "") => {
  const map = {};

  jobs.forEach((existingJob) => {
    if (!existingJob?.id || existingJob.id === excludeJobId) return;
    if (["Completed", "Invoice Sent", "Deleted"].includes(existingJob.status)) return;

    const assignedTechs = getJobAssignedTechNames(existingJob);
    if (!assignedTechs.length) return;

    const startKey = scheduleDateKey(existingJob.startDate || existingJob.occurrenceDate);
    if (!startKey) return;

    const estimatedDays = Math.max(1, Number(existingJob.jobPlan?.estimatedDays || 1));
    const endKey = scheduleDateKey(existingJob.endDate) || addWeekdays(startKey, estimatedDays - 1);
    const perTechHours = hoursFromScheduleWindow(existingJob.startTime, existingJob.endTime);

    const cursor = moment(startKey, "YYYY-MM-DD");
    const end = moment(endKey, "YYYY-MM-DD");

    while (cursor.isSameOrBefore(end, "day")) {
      if (cursor.day() !== 0 && cursor.day() !== 6) {
        const key = cursor.format("YYYY-MM-DD");

        assignedTechs.forEach((techName) => {
          if (!map[techName]) map[techName] = {};
          if (!map[techName][key]) map[techName][key] = { hours: 0, jobs: 0 };

          map[techName][key].hours += perTechHours;
          map[techName][key].jobs += 1;
        });
      }

      cursor.add(1, "day");
    }
  });

  return map;
};

const scoreTechForDateSpan = (techName, dateSpan, techLoadMap) => {
  return dateSpan.reduce((score, dateKey) => {
    const load = techLoadMap?.[techName]?.[dateKey] || { hours: 0, jobs: 0 };
    return score + load.hours + Math.max(0, load.jobs) * SCHEDULE_TRAVEL_BUFFER_HOURS;
  }, 0);
};

const findScheduleRecommendations = ({ draftJob, jobs, usersList, excludeJobId = "", skipDateKey = "" }) => {
  const estimatedDays = Math.max(1, Number(draftJob?.jobPlan?.estimatedDays || 0));
  const laborNeeded = Math.max(1, Number(draftJob?.jobPlan?.estimatedLabor || 0));

  if (!estimatedDays || !laborNeeded) {
    return {
      error: "Enter Estimated Days and Estimated Labor before using Recommend Schedule.",
      recommendations: [],
    };
  }

  const requiredSkills = inferRequiredSkillsForJob(draftJob);
  const activeStaff = usersList
    .filter((user) => (user.status || "Active") !== "Inactive")
    .filter((user) => !user.hideFromScheduling)
    .map((user) => ({
      ...user,
      displayName: getStaffDisplayName(user),
    }))
    .filter((user) => user.displayName);

  const skillMatchedStaff = activeStaff.filter((user) =>
    staffMatchesRequiredSkills(user, requiredSkills)
  );

  const candidateStaff = (skillMatchedStaff.length ? skillMatchedStaff : activeStaff)
    .filter((user) => user.displayName);

  if (candidateStaff.length < laborNeeded) {
    return {
      error: `Only ${candidateStaff.length} available staff match this job. Needed: ${laborNeeded}.`,
      recommendations: [],
    };
  }

  const techLoadMap = buildTechLoadMap(jobs, excludeJobId);
  const recommendations = [];
  const searchStart = moment().startOf("day");
  const searchEnd = moment().add(SCHEDULE_SEARCH_MONTHS, "months").endOf("day");
  const cursor = searchStart.clone();

  while (cursor.isSameOrBefore(searchEnd, "day") && recommendations.length < 5) {
    if (cursor.day() !== 0 && cursor.day() !== 6) {
      const startKey = cursor.format("YYYY-MM-DD");

      if (startKey !== skipDateKey) {
        const dateSpan = getWeekdaySpan(startKey, estimatedDays);
        const rankedTechs = candidateStaff
          .map((tech) => {
            const techName = tech.displayName;
            const totalLoad = scoreTechForDateSpan(techName, dateSpan, techLoadMap);
            const hasRoomEveryDay = dateSpan.every((dateKey) => {
              const load = techLoadMap?.[techName]?.[dateKey] || { hours: 0, jobs: 0 };
              const travelBuffer = load.jobs > 0 ? SCHEDULE_TRAVEL_BUFFER_HOURS : 0;
              return load.hours + travelBuffer + SCHEDULE_DAY_HOURS <= SCHEDULE_DAY_HOURS + 0.75;
            });

            return {
              ...tech,
              techName,
              totalLoad,
              hasRoomEveryDay,
            };
          })
          .filter((tech) => tech.hasRoomEveryDay)
          .sort((a, b) => a.totalLoad - b.totalLoad);

        if (rankedTechs.length >= laborNeeded) {
          const selectedTechs = rankedTechs.slice(0, laborNeeded);
          const workloadSummary = dateSpan.map((dateKey) => ({
            date: dateKey,
            techLoads: selectedTechs.map((tech) => {
              const load = techLoadMap?.[tech.techName]?.[dateKey] || { hours: 0, jobs: 0 };
              return {
                name: tech.techName,
                existingJobs: load.jobs,
                existingHours: load.hours,
              };
            }),
          }));

          recommendations.push({
            startDate: startKey,
            endDate: dateSpan[dateSpan.length - 1],
            techAssign: selectedTechs.map((tech) => tech.techName),
            requiredSkills,
            skillMatched: skillMatchedStaff.length > 0,
            workloadSummary,
          });
        }
      }
    }

    cursor.add(1, "day");
  }

  return { recommendations, requiredSkills };
};


const parseProposalDescriptionBlocks = (description = "") => {
  const source = String(description || "")
    .replace(/\r\n?/g, "\n")
    .replace(/\(\(\s*B\s*\)\)/g, "((b))")
    .replace(/\(\(\s*I\s*\)\)/g, "((i))")
    .replace(/\(\(\s*R\s*\)\)/g, "((r))");

  if (!source.trim()) return [];

  const markerRegex = /\(\((b|i|r|-)\)\)/gi;
  const matches = [...source.matchAll(markerRegex)];
  const blocks = [];

  if (!matches.length) {
    return source
      .split(/\n{2,}|\n/)
      .map((text) => ({ type: "r", text: text.trim() }))
      .filter((block) => block.text);
  }

  const leading = source.slice(0, matches[0].index).trim();
  if (leading) blocks.push({ type: "r", text: leading });

  matches.forEach((match, index) => {
    const start = match.index + match[0].length;
    const end = index + 1 < matches.length ? matches[index + 1].index : source.length;
    const text = source.slice(start, end).trim();
    if (text) blocks.push({ type: match[1].toLowerCase(), text });
  });

  return blocks;
};

const isDescriptionHeading = (text = "") => {
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (!clean) return false;
  if (clean.length <= 85 && !/[.!?]$/.test(clean)) return true;

  return /^(services?|introduction|scope|scope of service|treatment|treatment method|treatment areas?|high[- ]risk|inspection|service frequency|property management recommendations?|recommendations?|work to be performed|service steps?|application|areas? (included|treated))/i.test(clean);
};

const renderProposalJobDescription = (description, { compact = false } = {}) => {
  const blocks = parseProposalDescriptionBlocks(description);

  if (!blocks.length) {
    return <div style={{ color: "#6b7280" }}>No job description provided.</div>;
  }

  return blocks.map((block, index) => {
    const text = block.text.replace(/\s*\n\s*/g, " ").replace(/\s{2,}/g, " ").trim();
    if (!text) return null;

    if (block.type === "-") {
      return (
        <div key={index} style={{ display: "flex", gap: 8, margin: "5px 0 5px 14px", lineHeight: 1.5 }}>
          <span aria-hidden="true">•</span>
          <span>{text}</span>
        </div>
      );
    }

    if (block.type === "b" && isDescriptionHeading(text)) {
      return (
        <div
          key={index}
          style={{
            fontWeight: 800,
            fontSize: compact ? 16 : 17,
            marginTop: index === 0 ? 0 : 14,
            marginBottom: 6,
            lineHeight: 1.35,
          }}
        >
          {text}
        </div>
      );
    }

    if (block.type === "b") {
      return <div key={index} style={{ fontWeight: 700, marginBottom: 8, lineHeight: 1.55 }}>{text}</div>;
    }

    if (block.type === "i") {
      return <div key={index} style={{ fontStyle: "italic", marginBottom: 8, lineHeight: 1.55 }}>{text}</div>;
    }

    return <div key={index} style={{ marginBottom: 8, lineHeight: 1.55 }}>{text}</div>;
  });
};

const getTechDirectionBlocks = (description = "") => {
  const blocks = parseProposalDescriptionBlocks(description);
  if (!blocks.length) return [];

  const includeHeading = /(scope|treatment|high[- ]risk|inspection|application|service steps?|work to be performed|areas? (included|treated)|installation|repair|removal|exclusion|monitoring)/i;
  const excludeHeading = /(introduction|about us|property management recommendations?|recommendations?|service frequency|terms|warranty|pricing|investment|acceptance)/i;

  const selected = [];
  let includeCurrentSection = false;

  blocks.forEach((block) => {
    const clean = block.text.replace(/\s+/g, " ").trim();
    const heading = block.type === "b" && isDescriptionHeading(clean);

    if (heading) {
      if (excludeHeading.test(clean)) {
        includeCurrentSection = false;
        return;
      }

      includeCurrentSection = includeHeading.test(clean);
      if (includeCurrentSection) selected.push(block);
      return;
    }

    if (includeCurrentSection) selected.push(block);
  });

  if (selected.length) return selected;

  return blocks.filter((block) => {
    const clean = block.text.replace(/\s+/g, " ").trim();
    return (
      block.type === "-" ||
      /\b(inspect|inspection|treat|treatment|apply|application|install|repair|remove|service|monitor|bait|spray|larvicide|mist|equipment|areas? may include)\b/i.test(clean)
    );
  }).slice(0, 12);
};

const renderTechDirections = (description = "") => {
  const blocks = getTechDirectionBlocks(description);
  if (!blocks.length) return renderProposalJobDescription(description, { compact: true });

  return blocks.map((block, index) => {
    const text = block.text.replace(/\s*\n\s*/g, " ").replace(/\s{2,}/g, " ").trim();
    if (!text) return null;

    if (block.type === "-") {
      return (
        <div key={index} style={{ display: "flex", gap: 8, margin: "5px 0 5px 14px", lineHeight: 1.5 }}>
          <span aria-hidden="true">•</span>
          <span>{text}</span>
        </div>
      );
    }

    if (block.type === "b" && isDescriptionHeading(text)) {
      return (
        <div key={index} style={{ fontWeight: 800, fontSize: 16, marginTop: index ? 12 : 0, marginBottom: 5 }}>
          {text}
        </div>
      );
    }

    return <div key={index} style={{ marginBottom: 8, lineHeight: 1.5 }}>{text}</div>;
  });
};


// scheduleOnly mode: used by the calendar's Availability tab. Renders ONLY the
// Schedule Job popup (same form, same submit, same WO numbering, duplicate
// checks and tech email) so there is one schedule-job code path in the app.
//   prefill      { startDate, endDate, startTime, endTime, techAssign: [] }
//   onClose()    popup cancelled or success dialog closed
//   onScheduled(job)  fired after the job is saved
const Scheduling = ({ scheduleOnly = false, prefill = null, onClose, onScheduled } = {}) => {
  const [jobs, setJobs] = useState([]);
  const [jobTotals, setJobTotals] = useState({});
  const [userRole, setUserRole] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userFullName, setUserFullName] = useState("");
  const [currentUserMeta, setCurrentUserMeta] = useState(null);

  const [selectedJob, setSelectedJob] = useState(null);

  // Lock background scrolling while the Job Details popup is open so the
  // popup can't be scrolled past — it has to be closed to interact with
  // the page behind it.
  useEffect(() => {
    if (!selectedJob) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedJob]);
  const [selectedExportJobIds, setSelectedExportJobIds] = useState([]);
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]); // Update to handle multiple files
  const [uploading, setUploading] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [showCompleteConfirmPopup, setShowCompleteConfirmPopup] = useState(false);
  // "Job Scheduled Successfully!" confirmation after the Schedule Job form.
  const [showScheduledSuccess, setShowScheduledSuccess] = useState(false);
  // Set once the Complete Job & Create Invoice form is submitted.
  const completionSubmittedRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState(''); // Search term
  const [sortOption, setSortOption] = useState({ column: '', direction: '' }); // Sort option
  const [filteredJobs, setFilteredJobs] = useState([]); // Filtered jobs for search and sort
  const [companyWorkOrderSearch, setCompanyWorkOrderSearch] = useState(''); // Search by Company Work Order
  const [clientWorkOrderSearch, setClientWorkOrderSearch] = useState(''); // Search by Client Work Order
  // ===== Materials Popup =====
const [showMaterialsPopup, setShowMaterialsPopup] = useState(false);
const [materialsDraft, setMaterialsDraft] = useState([]);

const [isSubmittingJob, setIsSubmittingJob] = useState(false);
const submitLockRef = useRef(false);
const [scheduleRecommendations, setScheduleRecommendations] = useState([]);
const [recommendationTarget, setRecommendationTarget] = useState("new");
const [recommendationMessage, setRecommendationMessage] = useState("");
const [showRecommendationPanel, setShowRecommendationPanel] = useState(false);
const [selectedWOStem, setSelectedWOStem] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState({});
  const [job, setJob] = useState({
    siteName: '',
    management: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    email: '',
    phone: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    workOrder: '',
    serviceType: '',
    estimatedTotal: '',
    initialAmount: '',
    feeSchedule: 'Monthly',
    recurringInvoiceAmount: '',
    recurringFeeSchedule: 'Monthly',
    scope: '',
    jobDescription: '',
    files: [],
    techAssign: [],
    status: 'Active',
    referral: '',
    companyUse: '',
    companyUseOther: '',
    clientWorkOrder: '',
    isContractJob: false,
    contractBillingFrequency: 'Monthly',
    contractMonthlyAmount: '',
    contractInitialAmount: '',
    contractStartDate: '',
    contractEndDate: '',
isProject: false,
projectStatus: "",
projectRootId: "",
projectDayNumber: 1,
projectCompletedAt: "",
projectCompletedBy: "",
jobPlan: {
  estimatedDays: '',
  estimatedLabor: '',
  materials: []
},
recurrence: {
      type: "none",
      interval: 1,
      daysOfWeek: [],
      customDates: [],
      timesPerWeek: null,
      endsOn: ""
    }
  });
  const [isScheduling, setIsScheduling] = useState(false);
  const [woNumber, setWoNumber] = useState(1000);
  const [usersList, setUsersList] = useState([]);
  const [propertiesList, setPropertiesList] = useState([]);
  const [serviceTypeTemplates, setServiceTypeTemplates] = useState([]);
  const [showDeletePopup, setShowDeletePopup] = useState(false); // Updated state
  const [showTotalPopup, setShowTotalPopup] = useState(false);
  const [totalInput, setTotalInput] = useState("");
  const [rewritingJobDescription, setRewritingJobDescription] = useState(false);
  const [rewritingEditedJobDescription, setRewritingEditedJobDescription] = useState(false);

  const canSeeSupervisorNotes = ["Admin", "Manager", "Assistant"].includes(userRole);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const reopenJobId = location.state?.reopenJobId;
    if (!reopenJobId || !jobs.length) return;

    const occurrenceDate = location.state?.reopenOccurrenceDate || "";
    const match = jobs.find((job) => {
      if (job.id !== reopenJobId) return false;
      if (!occurrenceDate) return true;
      return (job.occurrenceDate || job.startDate || "") === occurrenceDate || job.id === reopenJobId;
    });

    if (match) setSelectedJob(match);
  }, [location.state, jobs]);

  const functions = getFunctions();
  const sendJobNoteEmailCallable = httpsCallable(functions, "sendJobNoteEmail");

const rewriteJobDescriptionCallable = httpsCallable(functions, "rewriteJobDescription");

const sendScheduledJobEmailCallable = httpsCallable(functions, "sendScheduledJobEmail");

  const serviceTypes = [
    "Action Items",
    "Inspection",
    "Meeting",
    "Proposal",
    "One-Time Service",
    "BED - Bed Bug Treatment",
    "HTT - Home Termite Treatment",
    "MPC - Monthly Pest Control",
    "QPC - Quarterly Pest Control",
    "RBS - Rodent Bait Station Service",
    "SEN - Termite Monitoring Station Service",
    "STT - Settlement Termite Treatment",
    "WDI - Wood Destroying Insect Inspection",
    "WLW - Wildlife Removal/Exclusion",
    "WPC - Warranty Pest Control",
    "WYG - Warranty Yard Guard",
    "YG - Yard Guard",
  ];

  const timeWindowOptions = [
    "ANY",
    "8AM - 10AM",
    "9AM - 11AM",
    "10AM - 12PM",
    "11AM - 1PM",
    "12PM - 2PM",
    "1PM - 3PM",
    "2PM - 4PM",
    "3PM - 5PM",
    "4PM - 6PM",
    "Custom Time"
  ];

  const woStemOptions = useMemo(() => buildWOStemOptions(jobs), [jobs]);
  const nextStemWOPreview = useMemo(
    () => getNextAvailableStemWOFromList(jobs, selectedWOStem),
    [jobs, selectedWOStem]
  );
  
  useEffect(() => {
        fetchUserData();
        fetchJobs();
        fetchUsers();
        fetchProperties();
        getDocs(collection(db, "proposalsPPDServiceTypes")).then((snap) => setServiceTypeTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() })))).catch(console.error);
        // show the next number in the form without incrementing the counter yet
        (async () => setWoNumber(await peekNextWoNumber()))();
      }, []);

  const fetchUserData = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (currentUser) {
      setUserId(currentUser.uid);

      const usersRef = collection(db, "users");
      const querySnapshot = await getDocs(usersRef);
      const userDoc = querySnapshot.docs.find((doc) => doc.id === currentUser.uid);

      if (userDoc) {
        setUserRole(userDoc.data().role);
        setUserFullName(`${userDoc.data().firstName} ${userDoc.data().lastName}`);
      
        setCurrentUserMeta({
          uid: currentUser.uid,
          firstName: userDoc.data().firstName,
          lastName: userDoc.data().lastName,
        });
      }
    }
  };

  console.log("✅ Scheduling.js LOADED - Feb 13 test");

  const fetchJobs = async () => {
    const jobsRef = collection(db, "jobs");
    const querySnapshot = await getDocs(jobsRef);
  
    // 🔥 Normalize each job so recurrence ALWAYS exists
    const allJobs = querySnapshot.docs.map((doc) => {
      const data = doc.data();
  
      return {
        id: doc.id,
        ...data,
        recurrence: data.recurrence || {
          type: "none",
          interval: 1,
          daysOfWeek: [],
          customDates: [],
          timesPerWeek: null,
          endsOn: ""
        }
      };
    });
  
    const hiddenServiceTypes = ["Approved Proposal", "Denied Proposal"];
  
    const VISIBLE_STATUSES = new Set(["active", "on hold", "monthly", ""]);

    const visibleJobs = allJobs
      .filter((job) =>
        VISIBLE_STATUSES.has((job.status || "").toString().toLowerCase().trim())
      )
      .map(job => ({
        ...job,
        siteName: job.siteName || "",
        management: job.management || "",
      }));    
    
    setJobs(visibleJobs);

    const totalsSnap = await getDocs(collection(db, "job_totals"));
    const totalsMap = {};
    totalsSnap.forEach((totalDoc) => {
      totalsMap[totalDoc.id] = totalDoc.data().total;
    });
    setJobTotals(totalsMap);
  };
  
  const toDateKey = (v) => (v ? moment(v).format("YYYY-MM-DD") : "");

  const getRecurrenceValue = (job) =>
    (job.recurrence || job.reccurance || job.recurring || job.repeat || "").toString().trim();
  
  const getRecurrenceType = (job) => {
    // New structure (object)
    if (job?.recurrence && typeof job.recurrence === "object") {
      return (job.recurrence.type || "none").toString().toLowerCase().trim();
    }
  
    // Old structure (string fields, just in case)
    const legacy =
      job?.reccurance ?? job?.recurring ?? job?.repeat ?? job?.recurrence ?? "none";
  
    return legacy.toString().toLowerCase().trim();
  };
  
  const isRecurringJob = (job) => {
    const type = (job?.recurrence?.type || "none").toLowerCase().trim();
    return type !== "none"; // "none" = one-time job
  };
  
  const possibleDuplicateJobs = useMemo(() => {
    if (!job.siteName && !job.scope && !job.jobDescription && !job.clientWorkOrder) {
      return [];
    }
  
    return findPossibleDuplicates(job, jobs);
  }, [job, jobs]);
  
  const getOccurrenceDateKey = (job) =>
    toDateKey(job.__renderDate || job.occurrenceDate || job.startDate);
  const fetchUsers = async () => {
    const querySnapshot = await getDocs(collection(db, 'users'));
    const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setUsersList(list);
  };

  const fetchProperties = async () => {
    const querySnapshot = await getDocs(collection(db, 'properties'));
    const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setPropertiesList(list);
  };

  useEffect(() => {
    if (showMaterialsPopup && selectedJob) {
      setMaterialsDraft(selectedJob.jobPlan?.materials || []);
    }
  }, [showMaterialsPopup, selectedJob]);
  

  useEffect(() => {
    filterAndSortJobs();
  }, [jobs, searchTerm, sortOption, companyWorkOrderSearch, clientWorkOrderSearch]);

  const closeModal = () => setSelectedJob(null);
  const closeNotePopup = () => setShowNotePopup(false);
  const closeVerificationPopup = () => {
    setShowVerificationPopup(false);
    if (completionSubmittedRef.current) {
      completionSubmittedRef.current = false;
      setSelectedJob(null);
      // Reload so SendInvoices / Calendar / Home reflect the completion.
      refreshKeepingView();
    }
  };

  const closeScheduledSuccess = () => {
    setShowScheduledSuccess(false);
    setIsScheduling(false);
    if (scheduleOnly && onClose) onClose();
  };

  // Availability tab: open straight into the Schedule Job popup, prefilled.
  useEffect(() => {
    if (!scheduleOnly) return;
    const p = prefill || {};
    const presetTimes = [
      "ANY", "8AM - 10AM", "9AM - 11AM", "10AM - 12PM", "11AM - 1PM",
      "12PM - 2PM", "1PM - 3PM", "2PM - 4PM", "3PM - 5PM", "4PM - 6PM",
    ];
    const startTime = p.startTime || "";
    setJob((prev) => ({
      ...prev,
      startDate: p.startDate || prev.startDate,
      endDate: p.endDate || p.startDate || prev.endDate,
      startTime,
      startTimeMode: !startTime || presetTimes.includes(startTime) ? "preset" : "custom",
      endTime: p.endTime || "",
      techAssign:
        Array.isArray(p.techAssign) && p.techAssign.filter(Boolean).length
          ? p.techAssign.filter(Boolean)
          : prev.techAssign,
    }));
    setSelectedWOStem("");
    setIsScheduling(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleOnly]);


  const markSourceProposalCompleted = async (job) => {
    if (!job?.proposalId) return;

    try {
      await updateDoc(doc(db, "proposals", job.proposalId), {
        status: "Completed",
        completedFromJobId: job.id || "",
        completedFromWO: job.wo || "",
        proposalCompletedAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn("Could not mark source proposal completed:", err);
    }
  };

  const markJobCompleted = async () => {
    if (!selectedJob) return;
  
    const jobRef = doc(db, "jobs", selectedJob.id);
    // Canonical (series) occurrence date. selectedJob.occurrenceDate is the
    // day a rescheduled occurrence moved to, which no expander looks for.
    const occurrenceKey = getCompletionOccurrenceKey(selectedJob);
  
    try {
      if (isRecurringJob(selectedJob)) {
        // Recurring jobs stay Active. Only this rendered occurrence date is completed/hidden.
        await updateDoc(jobRef, {
          completedDates: arrayUnion(occurrenceKey),
          [`completedAtByDate.${occurrenceKey}`]: new Date().toISOString(),
          updatedAt: serverTimestamp(),
        });

        setJobs((prev) =>
          prev.map((j) =>
            j.id === selectedJob.id
              ? {
                  ...j,
                  status: j.status || "Active",
                  completedDates: Array.from(new Set([...(j.completedDates || []), occurrenceKey])),
                }
              : j
          )
        );

        setShowVerificationPopup(false);
        setSelectedJob(null);

        // Reload so SendInvoices / Calendar / Home reflect this occurrence.
        refreshKeepingView();
        return;
      }

      await updateDoc(jobRef, {
        status: "Completed",
        completedDates: arrayUnion(occurrenceKey),
        updatedAt: serverTimestamp(),
        completedAt: serverTimestamp(),
      });
      await markSourceProposalCompleted(selectedJob);
  
      // update local UI
      setJobs((prev) =>
        prev.map((j) =>
          j.id === selectedJob.id
            ? { ...j, status: "Completed", completedDates: Array.from(new Set([...(j.completedDates || []), occurrenceKey])), completedAt: new Date().toISOString() }
            : j
        )
      );
  
      setShowVerificationPopup(false);
      setSelectedJob(null);

      refreshKeepingView();
    } catch (err) {
      console.error(err);
      alert("Could not mark job completed.");
    }
  };
  
  const addMaterial = () => {
    setMaterialsDraft((prev) => [
      ...prev,
      { name: "", qty: 1, unit: "pcs", notes: "" },
    ]);
  };
  
  const saveMaterials = async () => {
    if (!selectedJob?.id) return;
  
    try {
      const jobRef = doc(db, "jobs", selectedJob.id);
  
      // ✅ Merge into jobPlan without deleting other jobPlan fields
      const nextJobPlan = {
        ...(selectedJob.jobPlan || {}),
        materials: materialsDraft,
      };
  
      await updateDoc(jobRef, { jobPlan: nextJobPlan });
  
      // ✅ Keep UI in sync (both list + popup job)
      setJobs((prev) =>
        prev.map((j) =>
          j.id === selectedJob.id ? { ...j, jobPlan: nextJobPlan } : j
        )
      );
  
      setSelectedJob((prev) => (prev ? { ...prev, jobPlan: nextJobPlan } : prev));
  
      // If you're editing in-place and want the edit form to reflect it too:
      setEditedJob((prev) => (prev?.id === selectedJob.id ? { ...prev, jobPlan: nextJobPlan } : prev));
  
      setShowMaterialsPopup(false);
    } catch (err) {
      console.error("Error saving materials:", err);
      alert("Could not save materials. Please try again.");
    }
  };
  
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setSelectedJob((prev) => ({
      ...prev,
      newFiles: files.map(file => URL.createObjectURL(file))
    }));
  };

  const uploadFiles = async (files) => {
    const storage = getStorage();
    const uploadPromises = files.map(async (file) => {
      const storageRef = ref(storage, `notes/${selectedJob.id}/${file.name}`);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    });
    return await Promise.all(uploadPromises);
  };

  const addSupervisorNote = async () => {
    if (!selectedJob?.id) return;
  
    if (!canSeeSupervisorNotes) {
      alert("You do not have permission to add supervisor notes.");
      return;
    }
  
    const text = window.prompt("Add supervisor note:");
    if (!text || !text.trim()) return;
  
    const newSupervisorNote = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      text: text.trim(),
      author: userFullName || "User",
      authorRole: userRole || "",
      createdAt: new Date().toISOString(),
      checked: false,
      checkedBy: "",
      checkedAt: "",
    };
  
    const updatedSupervisorNotes = Array.isArray(selectedJob.supervisorNotes)
      ? [...selectedJob.supervisorNotes, newSupervisorNote]
      : [newSupervisorNote];
  
    await updateDoc(doc(db, "jobs", selectedJob.id), {
      supervisorNotes: updatedSupervisorNotes,
    });
  
    setJobs((prev) =>
      prev.map((job) =>
        job.id === selectedJob.id
          ? { ...job, supervisorNotes: updatedSupervisorNotes }
          : job
      )
    );
  
    setSelectedJob((prev) => ({
      ...prev,
      supervisorNotes: updatedSupervisorNotes,
    }));
  };

  const saveNote = async () => {
    if (!selectedJob || (noteText.trim() === "" && selectedFiles.length === 0)) {
      return;  // Nothing to save
    }
  
    setUploading(true);
  
    try {
      let fileUploads = [];
  
      if (selectedFiles.length > 0) {
        const storage = getStorage();
        fileUploads = await Promise.all(
          selectedFiles.map(async (file) => {
            const fileRef = ref(storage, `notes/${selectedJob.id}/${Date.now()}_${file.name}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            return { fileURL: url, fileName: file.name };
          })
        );
      }
  
      const newNote = {
        text: noteText,
        timestamp: new Date().toLocaleString(),
        author: userFullName || 'User',
        files: fileUploads
      };
  
      const jobDocRef = doc(db, 'jobs', selectedJob.id);
      const updatedNotes = Array.isArray(selectedJob.notes)
        ? [...selectedJob.notes, newNote]
        : [newNote];
  
      await updateDoc(jobDocRef, { notes: updatedNotes });

      try {
        await sendJobNoteEmailCallable({
          jobId: selectedJob.id,
          noteIndex: updatedNotes.length - 1,
        });
      } catch (emailErr) {
        console.error("Note saved, but note email failed:", emailErr);
      }

// ✅ update jobs list
setJobs(prev =>
  prev.map(j =>
    j.id === selectedJob.id
      ? { ...j, notes: updatedNotes }
      : j
  )
);

// ✅ keep selectedJob in sync
setSelectedJob(prev => ({
  ...prev,
  notes: updatedNotes,
}));

  
      setNoteText('');
      setSelectedFiles([]);
      setShowNotePopup(false);
    } catch (err) {
      console.error("Error saving note:", err);
      alert("There was an error saving your note. Please try again.");
    } finally {
      setUploading(false);
    }
  };
  

  const getBackgroundColor = (startDate) => {
    const date = moment(startDate);
    const now = moment();
    const diffDays = now.diff(date, 'days');

    if (diffDays > 30) {
      return 'red';
    } else if (diffDays > 1) {
      return 'orange';
    } else {
      return 'white';
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCompanyWorkOrderSearchChange = (e) => {
    setCompanyWorkOrderSearch(e.target.value);
  };

  const handleClientWorkOrderSearchChange = (e) => {
    setClientWorkOrderSearch(e.target.value);
  };

  const SortButton = ({ columnKey, sortOption, onSort }) => (
    <button onClick={() => onSort(columnKey)}>
      {sortOption.column === columnKey ? (sortOption.direction === 'asc' ? '↑' : '↓') : '↕'}
    </button>
  );

  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSortChange = (column) => {
    let direction = 'asc';
    if (sortOption.column === column && sortOption.direction === 'asc') {
      direction = 'desc';
    }
    setSortOption({ column, direction });
  };

  const getSchedulingExportKey = (job) =>
    `${job.id}_${job.occurrenceDate || toDateKey(job.startDate) || "UNSCHEDULED"}`;

  const toggleSchedulingExportJob = (jobKey) => {
    setSelectedExportJobIds((prev) =>
      prev.includes(jobKey)
        ? prev.filter((key) => key !== jobKey)
        : [...prev, jobKey]
    );
  };

  const getSchedulingJobTotal = (job = {}) => {
    const candidates = [
      jobTotals?.[job.id],
      job.estimatedTotal,
      job.jobTotal,
      job.total,
      job.amount,
    ];

    const found = candidates.find(
      (value) =>
        value !== "" &&
        value !== null &&
        value !== undefined &&
        Number.isFinite(Number(value))
    );

    return found === undefined ? 0 : Number(found);
  };

  const exportSelectedSchedulingJobsToExcel = () => {
    const sortedVisibleJobs = sortData(
      expandedJobs,
      sortOption.column,
      sortOption.direction
    );

    const selectedJobs = sortedVisibleJobs.filter((item) =>
      selectedExportJobIds.includes(getSchedulingExportKey(item))
    );

    if (!selectedJobs.length) {
      alert("No jobs selected for export.");
      return;
    }

    const includeRecommendations = window.confirm(
      "Would you like Excel to recommend a schedule for past-due jobs and jobs with no start date?\n\nClick OK for recommendations, or Cancel for a regular export."
    );

    const workingJobs = [...jobs];
    const recommendationMap = new Map();
    const todayKey = moment().format("YYYY-MM-DD");

    if (includeRecommendations) {
      selectedJobs.forEach((item) => {
        const currentStart = scheduleDateKey(
          item.startDate || item.occurrenceDate
        );

        const needsRecommendation =
          !currentStart || currentStart < todayKey;

        if (!needsRecommendation) return;

        const draftJob = {
          ...item,
          jobPlan: {
            ...(item.jobPlan || {}),
            estimatedDays: Math.max(
              1,
              Number(item.jobPlan?.estimatedDays || 1)
            ),
            estimatedLabor: Math.max(
              1,
              Number(item.jobPlan?.estimatedLabor || 1)
            ),
          },
        };

        const result = findScheduleRecommendations({
          draftJob,
          jobs: workingJobs,
          usersList,
          excludeJobId: item.id || "",
          skipDateKey: "",
        });

        const recommendation = result?.recommendations?.[0];
        if (!recommendation) return;

        recommendationMap.set(
          getSchedulingExportKey(item),
          {
            ...recommendation,
            requiredSkills:
              result.requiredSkills ||
              recommendation.requiredSkills ||
              [],
          }
        );

        workingJobs.push({
          ...item,
          id: `EXPORT_RECOMMENDATION_${item.id}_${recommendation.startDate}`,
          startDate: recommendation.startDate,
          endDate: recommendation.endDate,
          techAssign: recommendation.techAssign,
          status: "Active",
        });
      });
    }

    const rows = selectedJobs.map((item) => {
      const recommendation = recommendationMap.get(
        getSchedulingExportKey(item)
      );

      return {
        JobID: item.id || "",
        WorkOrder: item.wo || "",
        ClientWorkOrder: item.clientWorkOrder || "",
        SiteName: item.siteName || "",
        Management: item.management || "",
        Address: item.address || "",
        City: item.city || "",
        State: item.state || "",
        StartDate: item.startDate || "",
        EndDate: item.endDate || "",
        OccurrenceDate: item.occurrenceDate || "",
        StartTime: item.startTime || "",
        EndTime: item.endTime || "",
        ServiceType: item.serviceType || "",
        Scope: item.scope || "",
        JobDescription: item.jobDescription || "",
        TechAssign: Array.isArray(item.techAssign)
          ? item.techAssign.join(", ")
          : item.techAssign || "",
        Status: item.status || "",
        EstimatedDays: item.jobPlan?.estimatedDays || "",
        EstimatedLabor: item.jobPlan?.estimatedLabor || "",
        Materials: Array.isArray(item.jobPlan?.materials)
          ? item.jobPlan.materials
              .map(
                (m) =>
                  `${m.qty || ""} ${m.unit || ""} ${m.name || ""}${
                    m.notes ? ` (${m.notes})` : ""
                  }`
              )
              .join(" | ")
          : "",
        JobTotal: getSchedulingJobTotal(item),
        RecommendedStartDate: recommendation?.startDate || "",
        RecommendedEndDate: recommendation?.endDate || "",
        RecommendedTechAssign:
          recommendation?.techAssign?.join(", ") || "",
        RecommendedSkills:
          recommendation?.requiredSkills?.join(", ") || "",
        RecommendationReason: recommendation
          ? "Matched job description/service skills and checked current workload"
          : "",
      };
    });

    const totalAmount = selectedJobs.reduce(
      (sum, item) => sum + getSchedulingJobTotal(item),
      0
    );

    rows.push({
      JobID: "",
      WorkOrder: "",
      ClientWorkOrder: "",
      SiteName: "TOTAL",
      Management: "",
      Address: "",
      City: "",
      State: "",
      StartDate: "",
      EndDate: "",
      OccurrenceDate: "",
      StartTime: "",
      EndTime: "",
      ServiceType: "",
      Scope: "",
      JobDescription: "",
      TechAssign: "",
      Status: "",
      EstimatedDays: "",
      EstimatedLabor: "",
      Materials: "",
      JobTotal: totalAmount,
      RecommendedStartDate: "",
      RecommendedEndDate: "",
      RecommendedTechAssign: "",
      RecommendedSkills: "",
      RecommendationReason: "",
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    ws["!cols"] = Object.keys(rows[0]).map((key) => ({
      wch: Math.min(55, Math.max(12, key.length + 2)),
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Jobs");

    XLSX.writeFile(
      wb,
      `${
        includeRecommendations
          ? "Scheduling_Recommended_Schedule"
          : "Scheduling_Jobs_Export"
      }_${moment().format("YYYY-MM-DD_HH-mm")}.xlsx`
    );
  };

  const selectAllVisibleSchedulingJobs = () => {
    setSelectedExportJobIds(
      expandedJobs.map(getSchedulingExportKey)
    );
  };

  const clearSelectedSchedulingJobs = () => {
    setSelectedExportJobIds([]);
  };

  const clearSchedulingFilters = () => {
    setSearchTerm("");
    setCompanyWorkOrderSearch("");
    setClientWorkOrderSearch("");
    setSortOption({ column: "", direction: "" });
  };

  const filterAndSortJobs = () => {
    const term = searchTerm.toLowerCase();
  
    let filtered = jobs.filter((job) => {
      const siteName = (job.siteName || "").toLowerCase();
      const management = (job.management || "").toLowerCase();
  
      return (
        siteName.includes(term) ||
        management.includes(term)
      );
    });
  
    if (companyWorkOrderSearch) {
      filtered = filtered.filter(
        (job) =>
          job.wo &&
          job.wo.toString().includes(companyWorkOrderSearch)
      );
    }
  
    if (clientWorkOrderSearch) {
      filtered = filtered.filter(
        (job) =>
          (job.clientWorkOrder || "")
            .toLowerCase()
            .includes(clientWorkOrderSearch.toLowerCase())
      );
    }
  
    // 🔤 DEFAULT: alphabetical by site name
    filtered.sort((a, b) =>
      (a.siteName || "").localeCompare(b.siteName || "", undefined, {
        sensitivity: "base",
      })
    );
  
    setFilteredJobs(filtered);
  };
  
  const expandedJobs = filteredJobs.flatMap(job => {
    const status = String(job.status || "Active").toLowerCase().trim();
    const isClosed = ["completed", "invoice sent", "paid in full", "closed", "cancelled", "canceled", "deleted"].includes(status);

    // Hide closed jobs from the active Scheduling list.
    if (isClosed) {
      return [];
    }

    // Keep active jobs with no startDate visible so they can still be scheduled.
    const hasStartDate = Boolean(job.startDate);
    if (!hasStartDate) {
      return [{
        ...job,
        occurrenceDate: "",
        __renderDate: "",
        __unscheduled: true,
      }];
    }

    const dates = getOccurrencesForJob(
      job,
      moment().subtract(30, "days"),
      moment().add(60, "days")
    );
  
    // `date` is the canonical date the recurrence rule produced; a completion
    // may be recorded under that or under the day the occurrence was moved to.
    const isCompleted = makeOccurrenceDateMatcher(job, job.completedDates);
  
    return dates
      .filter(date => !isCompleted(date))
      .map(date => applyOccurrenceOverrideToRenderedJob({
        ...job,
        // Pin BOTH dates to this occurrence. Leaving the master endDate on a
        // rendered occurrence made the row claim a multi-day span it doesn't
        // have, which is the same endDate confusion that duplicated tiles on
        // Home and Calendar.
        startDate: date,
        endDate: date,
        occurrenceDate: date,
        originalOccurrenceDate: date,
        __renderDate: date,
      }));
  });

  const uniqueVisibleJobCount = useMemo(() => {
    return new Set(expandedJobs.map((item) => item.id).filter(Boolean)).size;
  }, [expandedJobs]);

  const unscheduledVisibleJobCount = useMemo(() => {
    return expandedJobs.filter((item) => item.__unscheduled || !item.startDate).length;
  }, [expandedJobs]);

  const scheduledVisibleJobCount = Math.max(0, uniqueVisibleJobCount - unscheduledVisibleJobCount);
  
  const handleSaveChanges = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!editedJob?.id || !selectedJob) return;

    try {
      const result = await saveRecurringJobEdit({
        originalJob: selectedJob,
        editedJob,
      });

      if (result.mode === "cancel") return;

      if (result.mode === "future") {
        setJobs((prevJobs) => [
          ...prevJobs.map((job) =>
            job.id === result.oldJob.id ? result.oldJob : job
          ),
          result.newJob,
        ]);
        setSelectedJob(result.newJob);
        setEditedJob(result.newJob);
      } else if (result.mode === "occurrence") {
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === result.masterJob.id ? result.masterJob : job
          )
        );
        setSelectedJob(result.updatedJob);
        setEditedJob(result.updatedJob);
      } else {
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === result.updatedJob.id ? result.updatedJob : job
          )
        );
        setSelectedJob(result.updatedJob);
        setEditedJob(result.updatedJob);
      }


      const savedTotal = editedJob.estimatedTotal;
      if (editedJob.siteName && editedJob.serviceType && savedTotal !== "" && savedTotal != null) {
        const property = propertiesList.find((item) => item.propertyName === editedJob.siteName);
        if (property?.id) {
          const code = getServiceCode(editedJob.serviceType);
          await updateDoc(doc(db, "properties", property.id), {
            [`serviceTypeTotals.${code}`]: Number(savedTotal),
          });
          setPropertiesList((prev) => prev.map((item) => item.id === property.id ? { ...item, serviceTypeTotals: { ...(item.serviceTypeTotals || {}), [code]: Number(savedTotal) } } : item));
        }
      }
      setIsEditing(false);
      alert("Job updated successfully!");
    } catch (error) {
      console.error("Error updating job:", error);
      alert("Failed to update job.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (submitLockRef.current) return;
  
    submitLockRef.current = true;
    setIsSubmittingJob(true);
  
    try {
      const resolvedCompanyUse =
        job.companyUse === "Other"
          ? (job.companyUseOther || "").trim()
          : job.companyUse;
  
      const draftJob = {
        ...job,
        companyUse: resolvedCompanyUse || "",
      };
  
      // 1) Fast local exact duplicate check
      const exactDuplicate = findExactDuplicate(draftJob, jobs);
  
      if (exactDuplicate) {
        alert(
          `This looks like an exact duplicate of WO #${exactDuplicate.wo || "Unknown"} for ${exactDuplicate.siteName}.`
        );
        return;
      }
  
      // 2) Optional Firestore check for same site + client WO
      if ((draftJob.clientWorkOrder || "").trim()) {
        const dupQuery = query(
          collection(db, "jobs"),
          where("siteName", "==", draftJob.siteName || ""),
          where("clientWorkOrder", "==", draftJob.clientWorkOrder.trim()),
          limit(5)
        );
  
        const dupSnap = await getDocs(dupQuery);
  
        const foundServerDuplicate = dupSnap.docs.find((d) => {
          const data = d.data();
          return data.status !== "Deleted";
        });
  
        if (foundServerDuplicate) {
          const serverDup = foundServerDuplicate.data();
          alert(
            `A job with Client WO #${draftJob.clientWorkOrder} already exists` +
            `${serverDup.wo ? ` (WO #${serverDup.wo})` : ""}.`
          );
          return;
        }
      }
  
      // 3) get the actual WO we're going to commit.
      // Normal jobs use the atomic counter. Stem jobs use the next open branch like 3345-2.
      const selectedStemRoot = getWOStemRoot(selectedWOStem);
      const assignedWO = selectedStemRoot
        ? await getNextAvailableStemWO(selectedStemRoot)
        : await getNextWoNumber();
  
      const jobData = {
        ...draftJob,
        wo: assignedWO,
        parentWOStem: selectedStemRoot || "",
        extendedFromWOStem: selectedStemRoot || "",
        isWOStemExtension: !!selectedStemRoot,
        creationDate: new Date(),
        createdBy: currentUserMeta
          ? {
              uid: currentUserMeta.uid,
              firstName: currentUserMeta.firstName,
              lastName: currentUserMeta.lastName,
            }
          : null,
      };
  
      delete jobData.files;
      delete jobData.companyUseOther;
      delete jobData.tempCustomDate;
  
      const jobDocRef = await addDoc(collection(db, "jobs"), jobData);

      if (jobData.isProject === true) {
        await updateDoc(jobDocRef, {
          projectRootId: jobDocRef.id,
          projectStatus: "Active",
          projectDayNumber: 1,
        });
      }

      try {
        await sendScheduledJobEmailCallable({
          jobId: jobDocRef.id,
        });
      } catch (emailErr) {
        console.error("Job saved, but tech email failed:", emailErr);
alert("Job was scheduled, but the technician email did not send. Check that the assigned tech has an email in the users collection.");
      }
  
      // Upload attachments if provided
      if (job.files.length > 0) {
        const storage = getStorage();
        const uploads = job.files.map(async (file) => {
          const storageRef = ref(storage, `jobs/${jobDocRef.id}/${file.name}`);
          await uploadBytes(storageRef, file);
          return await getDownloadURL(storageRef);
        });
  
        const fileURLs = await Promise.all(uploads);
  
        await updateDoc(jobDocRef, {
          fileURLs,
          fileNames: job.files.map((f) => f.name),
        });
      }
  
      const savedJobForCallback = { id: jobDocRef.id, ...jobData };
      setShowScheduledSuccess(true);
  
      setJob({
        siteName: "",
        management: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        email: "",
        phone: "",
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        workOrder: "",
        serviceType: "",
        scope: "",
        jobDescription: "",
        files: [],
        techAssign: [],
        status: "Active",
        referral: "",
        companyUse: "",
        companyUseOther: "",
        clientWorkOrder: '',
        isContractJob: false,
        contractBillingFrequency: 'Monthly',
        contractMonthlyAmount: '',
    contractInitialAmount: '',
        contractStartDate: '',
        contractEndDate: '',
        isProject: false,
        projectStatus: "",
        projectRootId: "",
        projectDayNumber: 1,
        projectCompletedAt: "",
        projectCompletedBy: "",
        jobPlan: {
          estimatedDays: "",
          estimatedLabor: "",
          materials: [],
        },
        recurrence: {
          type: "none",
          interval: 1,
          daysOfWeek: [],
          customDates: [],
          timesPerWeek: null,
          endsOn: "",
        },
      });
  
      setSelectedWOStem("");
      setWoNumber(await peekNextWoNumber());
      setIsScheduling(false);
      fetchJobs();
      if (onScheduled) {
        try { onScheduled(savedJobForCallback); } catch (cbErr) { console.error(cbErr); }
      }
    } catch (err) {
      console.error("Error:", err);
      alert("Failed to schedule job. Please try again.");
    } finally {
      submitLockRef.current = false;
      setIsSubmittingJob(false);
    }
  };

  const deleteJob = async (jobId) => {
    try {
      const jobRef = doc(db, "jobs", jobId);
      await updateDoc(jobRef, { status: "Deleted" });

      setJobs((prevJobs) => prevJobs.filter((job) => job.id !== jobId));
      setShowDeletePopup(false);
      closeModal();
      alert("Job deleted successfully!");
    } catch (error) {
      console.error("Error deleting job:", error);
      alert("Failed to delete job.");
    }
  };

  const handleDeleteJob = async () => {
    if (!selectedJob) return;
    try {
      await deleteDoc(doc(db, "jobs", selectedJob.id));
      setJobs(prev => prev.filter(job => job.id !== selectedJob.id));
      setShowDeletePopup(false);
      setSelectedJob(null);
      alert("Job deleted successfully.");
    } catch (error) {
      console.error("Error deleting job:", error);
      alert("Failed to delete job.");
    }
  };

  const persistSchedulingPricingOverride = async (draft, field, rawValue) => {
    const property = propertiesList.find((item) => item.propertyName === draft.siteName);
    if (!property?.id || !draft.serviceType) return;
    const code = getServiceCode(draft.serviceType);
    const current = readPropertyPricing(property, draft.serviceType);
    const value = field === "feeSchedule" ? rawValue : (rawValue === "" ? "" : Number(rawValue));
    const next = { ...current, [field]: value };
    await updateDoc(doc(db, "properties", property.id), { [`servicePricingOverrides.${code}`]: next });
    setPropertiesList((prev) => prev.map((item) => item.id === property.id ? { ...item, servicePricingOverrides: { ...(item.servicePricingOverrides || {}), [code]: next } } : item));
  };

  const handleSiteNameChange = (e) => {
    const selectedSiteName = e.target.value;
    const selectedProperty = propertiesList.find(property => property.propertyName === selectedSiteName);
    const template = serviceTypeTemplates.find((item) => item.serviceType === job.serviceType);
    const pricing = resolveServicePricing(selectedProperty, template, job.serviceType);
    setJob((prev) => ({
      ...prev, siteName: selectedSiteName, management: selectedProperty?.management || '', address: selectedProperty?.address || '',
      city: selectedProperty?.city || '', state: selectedProperty?.state || '', zip: selectedProperty?.zip || '',
      estimatedTotal: prev.serviceType ? pricing.amount : prev.estimatedTotal, initialAmount: prev.serviceType ? pricing.initialAmount : prev.initialAmount,
      feeSchedule: prev.serviceType ? pricing.feeSchedule : prev.feeSchedule, recurringInvoiceAmount: prev.serviceType ? pricing.amount : prev.recurringInvoiceAmount,
      recurringFeeSchedule: prev.serviceType ? pricing.feeSchedule : prev.recurringFeeSchedule,
    }));
  };

  const handleEditedSiteNameChange = (e) => {
    const selectedSiteName = e.target.value;
    const selectedProperty = propertiesList.find(property => property.propertyName === selectedSiteName);
    const template = serviceTypeTemplates.find((item) => item.serviceType === editedJob.serviceType);
    const pricing = resolveServicePricing(selectedProperty, template, editedJob.serviceType);
    setEditedJob((prev) => ({
      ...prev, siteName: selectedSiteName, management: selectedProperty?.management || '', address: selectedProperty?.address || '',
      city: selectedProperty?.city || '', state: selectedProperty?.state || '', zip: selectedProperty?.zip || '',
      estimatedTotal: prev.serviceType ? pricing.amount : prev.estimatedTotal, initialAmount: prev.serviceType ? pricing.initialAmount : prev.initialAmount,
      feeSchedule: prev.serviceType ? pricing.feeSchedule : prev.feeSchedule, recurringInvoiceAmount: prev.serviceType ? pricing.amount : prev.recurringInvoiceAmount,
      recurringFeeSchedule: prev.serviceType ? pricing.feeSchedule : prev.recurringFeeSchedule,
    }));
  };

  const handleTechnicianChange = (value) => {
    setEditedJob((prev) => ({
      ...prev,
      techAssign: value,
    }));
  };

  const addTechnicianField = () => {
    setJob((prevJob) => ({
      ...prevJob,
      techAssign: [...(prevJob.techAssign || []), ""],
    }));
  };
  
  const handleTechnicianFieldChange = (index, value) => {
    const updatedTechAssign = [...(job.techAssign || [])];
    updatedTechAssign[index] = value;
    setJob((prevJob) => ({
      ...prevJob,
      techAssign: updatedTechAssign,
    }));
  };
  
  const removeTechnicianField = (indexToRemove) => {
    setJob((prevJob) => ({
      ...prevJob,
      techAssign: (prevJob.techAssign || []).filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };
  
  const addEditedTechnicianField = () => {
    setEditedJob((prev) => ({
      ...prev,
      techAssign: [...(prev.techAssign || []), ""],
    }));
  };
  
  const removeEditedTechnicianField = (indexToRemove) => {
    setEditedJob((prev) => ({
      ...prev,
      techAssign: (prev.techAssign || []).filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };
  
  const openScheduleRecommendations = (target = "new", sourceJob = null) => {
    const draftJob = target === "new" ? job : sourceJob || editedJob || selectedJob;
    const originalStartDate = scheduleDateKey(sourceJob?.startDate || selectedJob?.startDate || "");

    const result = findScheduleRecommendations({
      draftJob,
      jobs,
      usersList,
      excludeJobId: target === "new" ? "" : (sourceJob?.id || selectedJob?.id || editedJob?.id || ""),
      skipDateKey: originalStartDate,
    });

    setRecommendationTarget(target);
    setRecommendationMessage(result.error || "");
    setScheduleRecommendations(result.recommendations || []);
    setShowRecommendationPanel(true);

    if (result.error) {
      alert(result.error);
    } else if (!result.recommendations?.length) {
      alert("No open schedule recommendation was found in the next 6 months with the current labor and skill limits.");
    }
  };

  const getRecommendationEndDateValue = (recommendation) => {
    if (!recommendation?.startDate) return "";
    return recommendation.endDate && recommendation.endDate !== recommendation.startDate
      ? recommendation.endDate
      : "";
  };

  const applyRecommendationDates = (recommendation, closePanel = false) => {
    if (!recommendation) return;

    const dateUpdate = {
      startDate: recommendation.startDate,
      endDate: getRecommendationEndDateValue(recommendation),
    };

    if (recommendationTarget === "new") {
      setJob((prev) => ({
        ...prev,
        ...dateUpdate,
      }));
    } else {
      setIsEditing(true);
      setEditedJob((prev) => ({
        ...(prev?.id ? prev : selectedJob),
        ...dateUpdate,
      }));
    }

    if (closePanel) setShowRecommendationPanel(false);
  };

  const applyRecommendationTechs = (recommendation, closePanel = false) => {
    if (!recommendation) return;

    if (recommendationTarget === "new") {
      setJob((prev) => ({
        ...prev,
        techAssign: recommendation.techAssign,
      }));
    } else {
      setIsEditing(true);
      setEditedJob((prev) => ({
        ...(prev?.id ? prev : selectedJob),
        techAssign: recommendation.techAssign,
      }));
    }

    if (closePanel) setShowRecommendationPanel(false);
  };

  const applyScheduleRecommendation = (recommendation) => {
    if (!recommendation) return;

    if (recommendationTarget === "new") {
      setJob((prev) => ({
        ...prev,
        startDate: recommendation.startDate,
        endDate: getRecommendationEndDateValue(recommendation),
        techAssign: recommendation.techAssign,
      }));
    } else {
      setIsEditing(true);
      setEditedJob((prev) => ({
        ...(prev?.id ? prev : selectedJob),
        startDate: recommendation.startDate,
        endDate: getRecommendationEndDateValue(recommendation),
        techAssign: recommendation.techAssign,
      }));
    }

    setShowRecommendationPanel(false);
  };

  const renderScheduleRecommendationPanel = () => {
    if (!showRecommendationPanel) return null;

    return (
      <div
        style={{
          border: "2px solid #2563eb",
          borderRadius: 10,
          padding: 12,
          margin: "12px 0",
          background: "#eff6ff",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
          <h3 style={{ marginTop: 0 }}>Recommended Schedule</h3>
          <button
            type="button"
            className="red-button"
            onClick={() => setShowRecommendationPanel(false)}
          >
            Close
          </button>
        </div>

        {recommendationMessage && (
          <p style={{ color: "#991b1b", fontWeight: 800 }}>{recommendationMessage}</p>
        )}

        {scheduleRecommendations.length === 0 && !recommendationMessage && (
          <p>No open schedule recommendation found.</p>
        )}

        {scheduleRecommendations.map((rec, index) => (
          <div
            key={`${rec.startDate}_${index}`}
            style={{
              border: "1px solid #93c5fd",
              borderRadius: 8,
              padding: 10,
              marginBottom: 10,
              background: "#fff",
            }}
          >
            <p style={{ margin: "0 0 6px 0" }}>
              <strong>Option {index + 1}:</strong> {rec.startDate} through {rec.endDate}
            </p>
            <p style={{ margin: "0 0 6px 0" }}>
              <strong>Recommended Techs:</strong> {rec.techAssign.join(", ")}
            </p>
            <p style={{ margin: "0 0 6px 0" }}>
              <strong>Matched Skills:</strong> {rec.requiredSkills.join(", ")}
              {!rec.skillMatched ? " (fallback staff used)" : ""}
            </p>
            <details>
              <summary>Workload check</summary>
              {rec.workloadSummary.map((day) => (
                <div key={day.date} style={{ marginTop: 6 }}>
                  <strong>{day.date}</strong>
                  <ul>
                    {day.techLoads.map((load) => (
                      <li key={`${day.date}_${load.name}`}>
                        {load.name}: {load.existingJobs} existing job(s), {load.existingHours} existing hour(s)
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </details>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
              <button
                type="button"
                className="blue-button"
                onClick={() => applyRecommendationDates(rec)}
              >
                Use Suggested Dates
              </button>
              <button
                type="button"
                className="blue-button"
                onClick={() => applyRecommendationTechs(rec)}
              >
                Use Suggested Techs
              </button>
              <button
                type="button"
                className="gray-button"
                onClick={() => applyScheduleRecommendation(rec)}
              >
                Use Dates + Techs
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };


  const handleRewriteJobDescription = async () => {
    try {
      setRewritingJobDescription(true);
  
      const result = await rewriteJobDescriptionCallable({
        mode: "scheduling",
        siteName: job.siteName || "",
        management: job.management || "",
        address: job.address || "",
        city: job.city || "",
        state: job.state || "",
        zip: job.zip || "",
        companyUse:
          job.companyUse === "Other"
            ? (job.companyUseOther || "")
            : (job.companyUse || ""),
        serviceType: job.serviceType || "",
        scope: job.scope || "",
        jobDescription: job.jobDescription || "",
        referral: job.referral || "",
      });
  
      const rewritten = result?.data?.rewrittenDescription || "";
  
      setJob((prev) => ({
        ...prev,
        jobDescription: rewritten,
      }));
    } catch (error) {
      console.error("Error rewriting job description:", error);
      alert("Could not rewrite the job description right now.");
    } finally {
      setRewritingJobDescription(false);
    }
  };
  
  const handleRewriteEditedJobDescription = async () => {
    try {
      setRewritingEditedJobDescription(true);
  
      const result = await rewriteJobDescriptionCallable({
        mode: "scheduling",
        siteName: editedJob.siteName || "",
        management: editedJob.management || "",
        address: editedJob.address || "",
        city: editedJob.city || "",
        state: editedJob.state || "",
        zip: editedJob.zip || "",
        companyUse: editedJob.companyUse || "",
        serviceType: editedJob.serviceType || "",
        scope: editedJob.scope || "",
        jobDescription: editedJob.jobDescription || "",
        referral: editedJob.referral || "",
      });
  
      const rewritten = result?.data?.rewrittenDescription || "";
  
      setEditedJob((prev) => ({
        ...prev,
        jobDescription: rewritten,
      }));
    } catch (error) {
      console.error("Error rewriting edited job description:", error);
      alert("Could not rewrite the job description right now.");
    } finally {
      setRewritingEditedJobDescription(false);
    }
  };

  return (
    <div className="scheduling-container">
      {!scheduleOnly && (
      <div style={{ textAlign: 'center', margin: '20px 0' }}>
      <button className="blue-button" onClick={async () => {
   setSelectedWOStem("");
   setWoNumber(await peekNextWoNumber());
   setIsScheduling(true);
 }}>Click Here to Schedule Job</button>
      </div>
      )}
      {isScheduling && createPortal(
        <>
        <div
          onClick={() => {
            setIsScheduling(false);
            if (scheduleOnly && onClose) onClose();
          }}
          style={{
            position: "fixed", inset: 0, width: "100vw", height: "100vh",
            background: "rgba(0,0,0,0.55)", zIndex: 1999998,
          }}
        />
          <div
            className="scheduling-container"
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", margin: 0,
              width: "min(760px, calc(100vw - 32px))", maxHeight: "88vh", overflowY: "auto",
              background: "#fff", padding: 20, borderRadius: 10,
              boxShadow: "0 10px 40px rgba(0,0,0,0.35)", textAlign: "left",
              boxSizing: "border-box", zIndex: 1999999,
            }}
          >
            <h3>Schedule Job</h3>
            <form className="form-container" onSubmit={handleSubmit}>
              <label>Site Name:</label>
              <select name="siteName" value={job.siteName} onChange={handleSiteNameChange} required>
                <option value="">Select Site Name</option>
                {propertiesList
                  .sort((a, b) => a.propertyName.localeCompare(b.propertyName)) // Sort properties alphabetically
                  .map(property => (
                    <option key={property.id} value={property.propertyName}>{property.propertyName}</option>
                  ))}
              </select>
              <label>Management:</label>
              <input type="text" name="management" value={job.management} onChange={(e) => setJob({ ...job, management: e.target.value })} />
              <label>Address:</label>
              <input type="text" name="address" value={job.address} onChange={(e) => setJob({ ...job, address: e.target.value })} />
              <label>City:</label>
              <input type="text" name="city" value={job.city} onChange={(e) => setJob({ ...job, city: e.target.value })} />
              <label>State:</label>
              <input type="text" name="state" value={job.state} onChange={(e) => setJob({ ...job, state: e.target.value })} />
              <label>Zip:</label>
              <input type="text" name="zip" value={job.zip} onChange={(e) => setJob({ ...job, zip: e.target.value })} />
              <label>Email:</label>
              <input type="email" name="email" value={job.email} onChange={(e) => setJob({ ...job, email: e.target.value })} />
              <label>Phone:</label>
              <input type="tel" name="phone" value={job.phone} onChange={(e) => setJob({ ...job, phone: e.target.value })} />
              <label>Work Order #:</label>
              <input
                type="text"
                name="workOrder"
                value={selectedWOStem ? (nextStemWOPreview || "Next branch will be calculated on save") : woNumber}
                readOnly
              />

              <label>Extend From Existing WO Stem (Optional):</label>
              <input
                list="wo-stem-options"
                value={selectedWOStem}
                onChange={(e) => setSelectedWOStem(e.target.value)}
                placeholder="Type or choose a WO stem, ex: 3345"
              />
              <datalist id="wo-stem-options">
                {woStemOptions.map((option) => (
                  <option
                    key={option.root}
                    value={option.root}
                    label={`${option.root} - ${option.siteName || "No Site"}`}
                  />
                ))}
              </datalist>

              {selectedWOStem && (
                <div
                  style={{
                    padding: 10,
                    background: "#ecfeff",
                    border: "1px solid #06b6d4",
                    borderRadius: 8,
                    marginBottom: 10,
                    fontWeight: 700,
                  }}
                >
                  This new job will be added under WO stem {getWOStemRoot(selectedWOStem)} as {nextStemWOPreview || "the next available branch"}.
                  <button
                    type="button"
                    className="red-button"
                    onClick={() => setSelectedWOStem("")}
                    style={{ marginLeft: 10 }}
                  >
                    Clear Stem
                  </button>
                </div>
              )}
              <label>Service Type:</label>
              <select name="serviceType" value={job.serviceType} onChange={(e) => {
                const serviceType = e.target.value;
                const template = serviceTypeTemplates.find((item) => item.serviceType === serviceType);
                const property = propertiesList.find((item) => item.propertyName === job.siteName);
                const pricing = resolveServicePricing(property, template, serviceType);
                const nextTotal = pricing.amount;
                const description = cleanTemplateText(template?.content || "");
                const scope = getServiceScope(serviceType);
                setJob((prev) => ({
                  ...prev,
                  serviceType,
                  // Example: "WPC - Warranty Pest Control" becomes
                  // "Warranty Pest Control".
                  scope,
                  // Always replace the prior service type's description.
                  // If no template exists, clear the old description.
                  jobDescription: description,
                  // Always replace the prior service price when service type changes.
                  estimatedTotal: nextTotal,
                  initialAmount: pricing.initialAmount,
                  feeSchedule: pricing.feeSchedule,
                  recurringInvoiceAmount: nextTotal,
                  recurringFeeSchedule: pricing.feeSchedule,
                }));
              }} required>
                <option value="">Select Service Type</option>
                {Array.from(new Set([...serviceTypes, ...serviceTypeTemplates.map((item) => item.serviceType).filter(Boolean)])).map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <label>Job Total (optional):</label>
              <input type="number" min="0" step="0.01" name="estimatedTotal" value={job.estimatedTotal ?? ""} onChange={(e) => setJob((prev) => ({ ...prev, estimatedTotal: e.target.value, recurringInvoiceAmount: e.target.value }))} placeholder="Uses the property override or service type default" onBlur={(e) => persistSchedulingPricingOverride(job, "amount", e.target.value)} />
              <label>Initial Amount:</label>
              <input type="number" min="0" step="0.01" value={job.initialAmount ?? ""} onChange={(e) => setJob((prev) => ({ ...prev, initialAmount: e.target.value }))} onBlur={(e) => persistSchedulingPricingOverride(job, "initialAmount", e.target.value)} />
              <label>Fee Schedule:</label>
              <select value={job.feeSchedule || "Monthly"} onChange={(e) => setJob((prev) => ({ ...prev, feeSchedule: e.target.value, recurringFeeSchedule: e.target.value }))} onBlur={(e) => persistSchedulingPricingOverride(job, "feeSchedule", e.target.value)}>
                {["Monthly", "Quarterly", "Every 6 Months", "Yearly", "One-Time", "Other"].map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
              <label>Scope:</label>
              <input type="text" name="scope" value={job.scope} onChange={(e) => setJob({ ...job, scope: e.target.value })} />
             <label>Job Description:</label>
<textarea
  name="jobDescription"
  value={job.jobDescription}
  onChange={(e) =>
    setJob({ ...job, jobDescription: e.target.value })
  }
  rows={5}
  style={{ resize: "vertical" }}
></textarea>

<div style={{ marginTop: "8px", marginBottom: "12px" }}>
  <button
    type="button"
    className="blue-button"
    onClick={handleRewriteJobDescription}
    disabled={rewritingJobDescription}
  >
    {rewritingJobDescription ? "Rewriting..." : "Rewrite with AI"}
  </button>
</div>
{/* ===== Job Planning (Optional) ===== */}
<h4 style={{ marginTop: 16 }}>Job Planning (Optional)</h4>

<label>Estimated Days</label>
<input
  type="number"
  min="1"
  value={job.jobPlan?.estimatedDays || ""}
  onChange={(e) =>
    setJob(prev => ({
      ...prev,
      jobPlan: {
        ...prev.jobPlan,
        estimatedDays: Number(e.target.value),
      },
    }))
  }
/>

<label>Estimated Labor (people)</label>
<input
  type="number"
  min="1"
  value={job.jobPlan?.estimatedLabor || ""}
  onChange={(e) =>
    setJob(prev => ({
      ...prev,
      jobPlan: {
        ...prev.jobPlan,
        estimatedLabor: Number(e.target.value),
      },
    }))
  }
/>

<button
  type="button"
  className="blue-button"
  onClick={() => openScheduleRecommendations("new", job)}
  style={{ margin: "10px 0" }}
>
  Recommend Schedule
</button>

{recommendationTarget === "new" && renderScheduleRecommendationPanel()}

              <label>Referral:</label>
              <input type="text" name="referral" value={job.referral} onChange={(e) => setJob({ ...job, referral: e.target.value })} />

              <div style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}>
  <label
    style={{
      display: "flex",
      gap: 8,
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "bold",
      padding: "10px 14px",
      border: "2px solid #ccc",
      borderRadius: "8px",
      background: "#f8f9fa",
      width: "fit-content",
      cursor: "pointer",
    }}
  >
    <input
      type="checkbox"
      checked={job.isProject === true}
      onChange={(e) =>
        setJob((prev) => ({
          ...prev,
          isProject: e.target.checked,
          projectStatus: e.target.checked ? "Active" : "",
          projectDayNumber: e.target.checked ? 1 : "",
        }))
      }
      style={{ width: "18px", height: "18px", margin: 0 }}
    />
    Project Job
  </label>
</div>

{job.isProject && (
  <div
    style={{
      padding: 10,
      background: "#ecfeff",
      border: "1px solid #06b6d4",
      borderRadius: 8,
      marginBottom: 10,
      fontWeight: 700,
    }}
  >
    This job will be treated as a multi-day project. Each daily completion can create the next project day.
  </div>
)}

              <label>Start Date:</label>
              <input type="date" name="startDate" value={job.startDate} onChange={(e) => setJob({ ...job, startDate: e.target.value })} />
              <label>End Date:</label>
              <input type="date" name="endDate" value={job.endDate} onChange={(e) => setJob({ ...job, endDate: e.target.value })} />
              <label>Start Time:</label>
              <select
  name="startTime"
  value={job.startTime ?? ""}
  onChange={(e) => {
    const value = e.target.value;

    if (value !== "Custom Time") {
      setJob((prev) => ({
        ...prev,
        startTime: value,
        startTimeMode: "preset",
      }));
    } else {
      setJob((prev) => ({
        ...prev,
        startTime: "",
        startTimeMode: "custom",
      }));
    }
  }}
>
  <option value="">Select Start Time</option>
  {timeWindowOptions.map((opt) => (
    <option key={opt} value={opt}>{opt}</option>
  ))}
</select>

{job.startTimeMode === "custom" && (
  <input
    type="time"
    value={job.startTime || ""}
    onChange={(e) =>
      setJob((prev) => ({ ...prev, startTime: e.target.value }))
    }
  />
)}
<label>End Time:</label>
<input
  type="time"
  name="endTime"
  value={job.endTime || ""}
  onChange={(e) =>
    setJob((prev) => ({ ...prev, endTime: e.target.value }))
  }
/>
{/* RECURRING JOB SETTINGS */}
<label>Recurrence:</label>
<select
  value={job.recurrence?.type || "none"}
  onChange={(e) =>
    setJob({
      ...job,
      recurrence: {
        ...job.recurrence,
        type: e.target.value,
      },
    })
  }
>
<option value="none">One-Time Job</option>
<option value="daily">Daily</option>
<option value="weekly">Weekly</option>
<option value="biweekly">Bi-Weekly (Every 2 Weeks)</option>
<option value="monthly">Monthly</option>
<option value="bimonthly">Bi-Monthly (Every 2 Months)</option>
<option value="quarterly">Quarterly</option>
<option value="biyearly">Bi-Yearly (Every 6 Months)</option>
<option value="yearly">Yearly</option>
<option value="customDates">Custom Dates</option>

</select>

{/* WEEKLY OPTIONS */}
{job.recurrence?.type === "weekly" && (
  <>
    <label>Times Per Week (optional):</label>
    <input
      type="number"
      min="1"
      max="7"
      value={job.recurrence.timesPerWeek || ""}
      onChange={(e) =>
        setJob({
          ...job,
          recurrence: {
            ...job.recurrence,
            timesPerWeek: Number(e.target.value),
          },
        })
      }
    />

    <label>Select Days:</label>
    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, index) => (
        <label key={index}>
          <input
            type="checkbox"
            checked={job.recurrence.daysOfWeek.includes(index)}
            onChange={() => {
              const newDays = job.recurrence.daysOfWeek.includes(index)
                ? job.recurrence.daysOfWeek.filter((d) => d !== index)
                : [...job.recurrence.daysOfWeek, index];

              setJob({
                ...job,
                recurrence: {
                  ...job.recurrence,
                  daysOfWeek: newDays,
                },
              });
            }}
          />
          {day}
        </label>
      ))}
    </div>
  </>
)}

{/* CUSTOM DATES */}
{job.recurrence?.type === "customDates" && (
  <>
    <label>Add Custom Date:</label>
    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
      <input
        type="date"
        value={job.tempCustomDate || ""}
        onChange={(e) =>
          setJob((prev) => ({ ...prev, tempCustomDate: e.target.value }))
        }
      />

      <button
        type="button"
        className="blue-button"
        onClick={() => {
          if (!job.tempCustomDate) return;

          setJob((prev) => ({
            ...prev,
            recurrence: {
              ...prev.recurrence,
              customDates: [...prev.recurrence.customDates, prev.tempCustomDate],
            },
            tempCustomDate: "", // reset input
          }));
        }}
      >
        Add
      </button>
    </div>

    <ul>
      {job.recurrence.customDates.map((d, i) => (
        <li key={i}>{d}</li>
      ))}
    </ul>
  </>
)}

<label>Select Company:</label>
<select
  name="companyUse"
  value={job.companyUse || ""}
  onChange={(e) =>
    setJob((prev) => ({
      ...prev,
      companyUse: e.target.value,
      companyUseOther: e.target.value === "Other" ? prev.companyUseOther || "" : ""
    }))
  }
>
  <option value="">Select Company</option>
  <option value="JMC">JMC</option>
  <option value="DCS">DCS</option>
  <option value="OGR">OGR</option>
  <option value="MHI">MHI</option>
  <option value="Other">Other</option>
</select>

{job.companyUse === "Other" && (
  <>
    <label>Other Company Name:</label>
    <input
      type="text"
      value={job.companyUseOther || ""}
      onChange={(e) =>
        setJob((prev) => ({
          ...prev,
          companyUseOther: e.target.value
        }))
      }
      placeholder="Enter company name"
    />
  </>
)}

              <label>Client Work Order #:</label>
              <input type="text" name="clientWorkOrder" value={job.clientWorkOrder} onChange={(e) => setJob({ ...job, clientWorkOrder: e.target.value })} />
              <label>Upload Files:</label>
              <input type="file" multiple onChange={(e) => setJob({ ...job, files: Array.from(e.target.files) })} />
              <label>Tech Assign:</label>

{(job.techAssign || []).map((tech, index) => (
  <div
    key={index}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "8px",
    }}
  >
    <select
      value={tech}
      onChange={(e) => handleTechnicianFieldChange(index, e.target.value)}
      required
      style={{ flex: 1 }}
    >
      <option value="">Select Technician</option>
      {usersList.map((user) => (
        <option key={user.id} value={`${user.firstName} ${user.lastName}`}>
          {user.firstName} {user.lastName}
        </option>
      ))}
    </select>

    <button
      type="button"
      className="red-button"
      onClick={() => removeTechnicianField(index)}
      style={{
        width: "44px",
        minWidth: "44px",
        height: "44px",
        padding: 0,
        fontSize: "18px",
        fontWeight: "bold",
      }}
      title="Remove technician"
    >
      X
    </button>
  </div>
))}

<button type="button" className="blue-button" onClick={addTechnicianField}>
  Add Technician
</button>
              <button className="blue-button" type="submit" disabled={isSubmittingJob}>
  {isSubmittingJob ? "Scheduling..." : "Schedule Job"}
</button>
              <button
                className="red-button"
                type="button"
                onClick={() => {
                  setIsScheduling(false);
                  if (scheduleOnly && onClose) onClose();
                }}
              >
                Cancel
              </button>
            </form>
          </div>
        </>,
        document.body
      )}

      {showScheduledSuccess && createPortal(
        <>
          <div
            onClick={closeScheduledSuccess}
            style={{
              position: "fixed", inset: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.55)", zIndex: 2999998,
            }}
          />
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", margin: 0,
              width: "90%", maxWidth: 420, background: "#fff", padding: 20,
              borderRadius: 10, boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
              textAlign: "center", boxSizing: "border-box", zIndex: 2999999,
            }}
          >
            <h3 style={{ marginTop: 0 }}>Job Scheduled Successfully!</h3>
            <button className="shortgreen-button" onClick={closeScheduledSuccess}>OK</button>
          </div>
        </>,
        document.body
      )}

      {!scheduleOnly && (<>
      <div className="dashboard-section">
        <h2>Jobs</h2>
        <div className="search-sort-container">
          <input
            type="text"
            placeholder="Search by Site Name or Management"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <input
            type="text"
            placeholder="Search by Company Work Order"
            value={companyWorkOrderSearch}
            onChange={handleCompanyWorkOrderSearchChange}
          />
          <input
            type="text"
            placeholder="Search by Client Work Order"
            value={clientWorkOrderSearch}
            onChange={handleClientWorkOrderSearchChange}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
            margin: "12px 0",
            padding: "10px 12px",
            border: "1px solid #bfdbfe",
            borderRadius: 10,
            background: "#eff6ff",
            fontWeight: 800,
          }}
        >
          <span>Total Active Jobs: {uniqueVisibleJobCount}</span>
          <span>Scheduled: {scheduledVisibleJobCount}</span>
          <span>Unscheduled: {unscheduledVisibleJobCount}</span>
          <span>Visible Rows: {expandedJobs.length}</span>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            margin: "0 0 12px",
            alignItems: "center",
          }}
        >
          <button
            type="button"
            onClick={selectAllVisibleSchedulingJobs}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #1d4ed8",
              background: "#e0e7ff",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Select All Filtered
          </button>

          <button
            type="button"
            onClick={clearSelectedSchedulingJobs}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #dc2626",
              background: "#fee2e2",
              color: "#991b1b",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Clear Selected
          </button>

          <button
            type="button"
            onClick={exportSelectedSchedulingJobsToExcel}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #16a34a",
              background: "#dcfce7",
              color: "#166534",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Export Selected to Excel
          </button>

          <button
            type="button"
            onClick={clearSchedulingFilters}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #dc2626",
              background: "#fee2e2",
              color: "#991b1b",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            ✖ Clear Filters
          </button>

          <span style={{ fontWeight: 700 }}>
            Selected: {selectedExportJobIds.length}
          </span>
        </div>

        {expandedJobs.length > 0 ? (
          <div
          className="table-container"
          style={{
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: "70vh", // critical
          }}
        >
        
            <table>
              <thead>
                <tr>
                  <th style={{ width: 42 }}>Select</th>
                  <th>
                    WO# <SortButton columnKey="wo" sortOption={sortOption} onSort={handleSortChange} />
                  </th>
                  <th>
                    Site Name <SortButton columnKey="siteName" sortOption={sortOption} onSort={handleSortChange} />
                  </th>
                  <th>
                    City <SortButton columnKey="city" sortOption={sortOption} onSort={handleSortChange} />
                  </th>
                  <th>
                    Start Date <SortButton columnKey="startDate" sortOption={sortOption} onSort={handleSortChange} />
                  </th>
                  <th>
                    Service Type <SortButton columnKey="serviceType" sortOption={sortOption} onSort={handleSortChange} />
                  </th>
                  <th>Scope</th>
                  <th>
                    Tech <SortButton columnKey="techAssign" sortOption={sortOption} onSort={handleSortChange} />
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
  {sortData(expandedJobs, sortOption.column, sortOption.direction).map((job) => (
    <tr
      key={`${job.id}-${job.occurrenceDate}`}
      style={{
        backgroundColor: getBackgroundColor(job.occurrenceDate || job.startDate),
      }}
    >
      <td>
        <input
          type="checkbox"
          checked={selectedExportJobIds.includes(getSchedulingExportKey(job))}
          onChange={() => toggleSchedulingExportJob(getSchedulingExportKey(job))}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select work order ${job.wo || ""} for export`}
        />
      </td>
      <td>{job.wo}</td>
      <td>{job.siteName}</td>
      <td>{job.city}</td>

      <td>
        {job.occurrenceDate || job.startDate}
        {job.recurrence?.type === "customDates" && (
          <span style={{ marginLeft: "6px", color: "blue", fontWeight: "bold" }}>
            (Custom)
          </span>
        )}
      </td>

      <td>{job.serviceType}</td>
      <td>{job.scope}</td>
      <td>{Array.isArray(job.techAssign) ? job.techAssign.join(", ") : job.techAssign}</td>

      <td>
        <button className="azul-button" onClick={() => setSelectedJob(job)}>
          Details
        </button>
      </td>
    </tr>
  ))}
</tbody>

            </table>
          </div>
        ) : (
          <p>No jobs available.</p>
        )}
      </div>

      {selectedJob && createPortal(
        <>
          <div
            onClick={closeModal}
            style={{
              position: "fixed",
              inset: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.55)",
              zIndex: 999998,
            }}
          ></div>
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              margin: 0,
              width: "min(1000px, calc(100vw - 40px))",
              maxHeight: "85vh",
              overflowY: "auto",
              background: "#fff",
              padding: 20,
              borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
              textAlign: "left",
              boxSizing: "border-box",
              zIndex: 999999,
            }}
          >
            <h2>Job Details</h2>

            {isEditing ? (
              <form className="form-container">
                <label>Site Name:</label>
                <select
                  name="siteName"
                  value={editedJob.siteName || ''}
                  onChange={handleEditedSiteNameChange}
                  required
                >
                  <option value="">Select Site Name</option>
                  {propertiesList
                    .sort((a, b) => a.propertyName.localeCompare(b.propertyName)) // Sort properties alphabetically
                    .map((property) => (
                      <option key={property.id} value={property.propertyName}>
                        {property.propertyName}
                      </option>
                    ))}
                </select>
                <label>Address:</label>
                <input
                  type="text"
                  name="address"
                  value={editedJob.address || ''}
                  onChange={(e) => setEditedJob(prev => ({ ...prev, address: e.target.value }))}
                  readOnly
                />
                <label>City:</label>
                <input
                  type="text"
                  name="city"
                  value={editedJob.city || ''}
                  onChange={(e) => setEditedJob(prev => ({ ...prev, city: e.target.value }))}
                  readOnly
                />
                <label>State:</label>
                <input
                  type="text"
                  name="state"
                  value={editedJob.state || ''}
                  onChange={(e) => setEditedJob(prev => ({ ...prev, state: e.target.value }))}
                  readOnly
                />
                <label>Start Date:</label>
                <input
                  type="date"
                  name="startDate"
                  value={editedJob.startDate || ''}
                  onChange={(e) => setEditedJob(prev => ({ ...prev, startDate: e.target.value }))}
                />
                <label>End Date:</label>
                <input
                  type="date"
                  name="endDate"
                  value={editedJob.endDate || ''}
                  onChange={(e) => setEditedJob(prev => ({ ...prev, endDate: e.target.value }))}
                />
                <label>Start Time:</label>
<select
  name="startTime"
  value={editedJob.startTime}
  onChange={(e) => {
    const value = e.target.value;
    if (value !== "Custom Time") {
      setEditedJob((prev) => ({ ...prev, startTime: value }));
    } else {
      setEditedJob((prev) => ({ ...prev, startTime: "" }));
    }
  }}
>
  <option value="">Select Start Time</option>
  {timeWindowOptions.map((option) => (
    <option key={option} value={option}>{option}</option>
  ))}
</select>

{editedJob.startTime === "" && (
  <input
    type="time"
    name="startTime"
    value={editedJob.startTime || ""}
    onChange={(e) => setEditedJob((prev) => ({ ...prev, startTime: e.target.value }))}
  />
)}

                <label>Service Type:</label>
                <select
                  name="serviceType"
                  value={editedJob.serviceType || ''}
                  onChange={(e) => {
                    const serviceType = e.target.value;
                    const template = serviceTypeTemplates.find((item) => item.serviceType === serviceType);
                    const property = propertiesList.find((item) => item.propertyName === editedJob.siteName);
                    const propertyTotal = getPropertyServiceTotal(property, serviceType);
                    const templateTotal =
                      template?.total !== "" && template?.total != null
                        ? template.total
                        : "";
                    const nextTotal =
                      propertyTotal !== "" ? propertyTotal : templateTotal;
                    const description = cleanTemplateText(template?.content || "");
                    const scope = getServiceScope(serviceType);
                    const pricing = resolveServicePricing(property, template, serviceType);
                    setEditedJob((prev) => ({
                      ...prev,
                      serviceType,
                      scope,
                      // Changing the service type must also replace or clear
                      // the previous job description.
                      jobDescription: description,
                      estimatedTotal: nextTotal,
                      initialAmount: pricing.initialAmount,
                      feeSchedule: pricing.feeSchedule,
                      recurringInvoiceAmount: nextTotal,
                      recurringFeeSchedule: pricing.feeSchedule,
                    }));
                  }}
                  required
                >
                  <option value="">Select Service Type</option>
                  {Array.from(new Set([...serviceTypes, ...serviceTypeTemplates.map((item) => item.serviceType).filter(Boolean)])).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <label>Total:</label>
                <input type="number" min="0" step="0.01" value={editedJob.estimatedTotal ?? ""} onChange={(e) => setEditedJob(prev => ({ ...prev, estimatedTotal: e.target.value, recurringInvoiceAmount: e.target.value }))} onBlur={(e) => persistSchedulingPricingOverride(editedJob, "amount", e.target.value)} />
                <label>Initial Amount:</label>
                <input type="number" min="0" step="0.01" value={editedJob.initialAmount ?? ""} onChange={(e) => setEditedJob(prev => ({ ...prev, initialAmount: e.target.value }))} onBlur={(e) => persistSchedulingPricingOverride(editedJob, "initialAmount", e.target.value)} />
                <label>Fee Schedule:</label>
                <select value={editedJob.feeSchedule || "Monthly"} onChange={(e) => setEditedJob(prev => ({ ...prev, feeSchedule: e.target.value, recurringFeeSchedule: e.target.value }))} onBlur={(e) => persistSchedulingPricingOverride(editedJob, "feeSchedule", e.target.value)}>
                  {["Monthly", "Quarterly", "Every 6 Months", "Yearly", "One-Time", "Other"].map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
                <label>Scope:</label>
                <input
                  type="text"
                  name="scope"
                  value={editedJob.scope || ''}
                  onChange={(e) => setEditedJob(prev => ({ ...prev, scope: e.target.value }))}
                />
                <label>Select Company:</label>
<select
  name="companyUse"
  value={
    ["JMC", "DCS", "OGR", "MHI", "Other"].includes(editedJob.companyUse)
      ? editedJob.companyUse
      : (editedJob.companyUse ? "Other" : "")
  }
  onChange={(e) => {
    const value = e.target.value;

    if (value === "Other") {
      setEditedJob((prev) => ({
        ...prev,
        companyUse:
          !["JMC", "DCS", "OGR", "MHI"].includes(prev.companyUse)
            ? prev.companyUse || ""
            : "",
        companyUseMode: "Other"
      }));
    } else {
      setEditedJob((prev) => ({
        ...prev,
        companyUse: value,
        companyUseMode: value
      }));
    }
  }}
>
  <option value="">Select Company</option>
  <option value="JMC">JMC</option>
  <option value="DCS">DCS</option>
  <option value="OGR">OGR</option>
  <option value="MHI">MHI</option>
  <option value="Other">Other</option>
</select>

{(
  editedJob.companyUseMode === "Other" ||
  (editedJob.companyUse &&
    !["JMC", "DCS", "OGR", "MHI"].includes(editedJob.companyUse))
) && (
  <>
    <label>Other Company Name:</label>
    <input
      type="text"
      value={
        ["JMC", "DCS", "OGR", "MHI"].includes(editedJob.companyUse)
          ? ""
          : editedJob.companyUse || ""
      }
      onChange={(e) =>
        setEditedJob((prev) => ({
          ...prev,
          companyUse: e.target.value,
          companyUseMode: "Other"
        }))
      }
      placeholder="Enter company name"
    />
  </>
)}
       <label>Job Description:</label>
<textarea
  name="jobDescription"
  value={editedJob.jobDescription || ""}
  onChange={(e) =>
    setEditedJob(prev => ({
      ...prev,
      jobDescription: e.target.value
    }))
  }
  rows={5}
  style={{ resize: "vertical" }}
></textarea>

<div style={{ marginTop: "8px", marginBottom: "12px" }}>
  <button
    type="button"
    className="blue-button"
    onClick={handleRewriteEditedJobDescription}
    disabled={rewritingEditedJobDescription}
  >
    {rewritingEditedJobDescription ? "Rewriting..." : "Rewrite with AI"}
  </button>
</div>

<div style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}>
  <label
    style={{
      display: "flex",
      gap: 8,
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "bold",
      padding: "10px 14px",
      border: "2px solid #ccc",
      borderRadius: "8px",
      background: "#f8f9fa",
      width: "fit-content",
      cursor: "pointer",
    }}
  >
    <input
      type="checkbox"
      checked={editedJob.isProject === true}
      onChange={(e) =>
        setEditedJob((prev) => ({
          ...prev,
          isProject: e.target.checked,
          projectStatus: e.target.checked
            ? prev.projectStatus || "Active"
            : "",
          projectRootId: e.target.checked
            ? prev.projectRootId || prev.id || selectedJob.id
            : "",
          projectDayNumber: e.target.checked
            ? Number(prev.projectDayNumber || 1)
            : "",
        }))
      }
      style={{ width: "18px", height: "18px", margin: 0 }}
    />
    Project Job
  </label>
</div>

{editedJob.isProject && (
  <div
    style={{
      padding: 10,
      background: "#ecfeff",
      border: "1px solid #06b6d4",
      borderRadius: 8,
      marginBottom: 10,
      fontWeight: 700,
    }}
  >
    Project is active. Daily completion can create the next project day from Home.
  </div>
)}

                <h4 style={{ marginTop: 16 }}>Job Planning</h4>

                <label>Estimated Days</label>
                <input
                  type="number"
                  min="1"
                  value={editedJob.jobPlan?.estimatedDays || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      jobPlan: {
                        ...(prev.jobPlan || {}),
                        estimatedDays: Number(e.target.value),
                      },
                    }))
                  }
                />

                <label>Estimated Labor (people)</label>
                <input
                  type="number"
                  min="1"
                  value={editedJob.jobPlan?.estimatedLabor || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      jobPlan: {
                        ...(prev.jobPlan || {}),
                        estimatedLabor: Number(e.target.value),
                      },
                    }))
                  }
                />

                <button
                  type="button"
                  className="blue-button"
                  onClick={() => openScheduleRecommendations("edit", editedJob)}
                  style={{ margin: "10px 0" }}
                >
                  Recommend Schedule
                </button>

                {recommendationTarget === "edit" && renderScheduleRecommendationPanel()}

                <label>Status:</label>
                <select
                  name="status"
                  value={editedJob.status || ''}
                  onChange={(e) => setEditedJob(prev => ({ ...prev, status: e.target.value }))}
                  required
                >
                  <option value="">Select Status</option>
                  {["Active", "On Hold", "Completed", "Invoice Sent", "Proposal Sent", "Monthly"].map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                <label>Recurrence:</label>
<select
  value={editedJob.recurrence?.type || "none"}
  onChange={(e) =>
    setEditedJob({
      ...editedJob,
      recurrence: {
        ...(editedJob.recurrence || {}),
        type: e.target.value,
      },
    })
  }
>
  <option value="none">One-Time Job</option>
  <option value="daily">Daily</option>
  <option value="weekly">Weekly</option>
  <option value="monthly">Monthly</option>
  <option value="quarterly">Quarterly</option>
  <option value="yearly">Yearly</option>
  <option value="customDates">Custom Dates</option>
</select>

{/* WEEKLY OPTIONS */}
{editedJob.recurrence?.type === "weekly" && (
  <>
    <label>Times Per Week:</label>
    <input
      type="number"
      value={editedJob.recurrence.timesPerWeek || ""}
      onChange={(e) =>
        setEditedJob({
          ...editedJob,
          recurrence: {
            ...editedJob.recurrence,
            timesPerWeek: Number(e.target.value),
          },
        })
      }
    />

    <label>Select Days:</label>
    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, i) => (
        <label key={i}>
          <input
            type="checkbox"
            checked={editedJob.recurrence.daysOfWeek?.includes(i)}
            onChange={() => {
              const newDays = editedJob.recurrence.daysOfWeek.includes(i)
                ? editedJob.recurrence.daysOfWeek.filter((x) => x !== i)
                : [...editedJob.recurrence.daysOfWeek, i];

              setEditedJob({
                ...editedJob,
                recurrence: {
                  ...editedJob.recurrence,
                  daysOfWeek: newDays,
                },
              });
            }}
          />
          {day}
        </label>
      ))}
    </div>
  </>
)}

{/* CUSTOM DATES */}
<label>Add Custom Date:</label>
<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
  <input
    type="date"
    value={editedJob.tempCustomDate || ""}
    onChange={(e) =>
      setEditedJob((prev) => ({ ...prev, tempCustomDate: e.target.value }))
    }
  />

  <button
    type="button"
    className="blue-button"
    onClick={() => {
      if (!editedJob.tempCustomDate) return;

      setEditedJob((prev) => ({
        ...prev,
        recurrence: {
          ...prev.recurrence,
          customDates: [...prev.recurrence.customDates, prev.tempCustomDate],
        },
        tempCustomDate: "", // clear after adding
      }));
    }}
  >
    Add
  </button>
</div>

<ul>
  {editedJob.recurrence.customDates?.map((d, i) => (
    <li key={i}>{d}</li>
  ))}
</ul>


<label>Tech Assign:</label>

{(editedJob.techAssign || []).map((tech, index) => (
  <div
    key={index}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "8px",
    }}
  >
    <select
      value={tech}
      onChange={(e) => {
        const updated = [...(editedJob.techAssign || [])];
        updated[index] = e.target.value;
        setEditedJob((prev) => ({ ...prev, techAssign: updated }));
      }}
      style={{ flex: 1 }}
    >
      <option value="">Select Technician</option>
      {usersList.map((u) => (
        <option key={u.id} value={`${u.firstName} ${u.lastName}`}>
          {u.firstName} {u.lastName}
        </option>
      ))}
    </select>

    <button
      type="button"
      className="red-button"
      onClick={() => removeEditedTechnicianField(index)}
      style={{
        width: "44px",
        minWidth: "44px",
        height: "44px",
        padding: 0,
        fontSize: "18px",
        fontWeight: "bold",
      }}
      title="Remove technician"
    >
      X
    </button>
  </div>
))}

<button
  type="button"
  className="blue-button"
  onClick={addEditedTechnicianField}
>
  Add Technician
</button>

<div className="modal-buttons">
  <button
    type="button"
    className="blue-button"
    onClick={handleSaveChanges}
  >
    Save
  </button>

  <button
    type="button"
    className="red-button"
    onClick={() => setIsEditing(false)}
  >
    Cancel
  </button>
</div>
              </form>
            ) : (
              <>
                <p><strong>Site Name:</strong> {selectedJob.siteName}</p>
                <p><strong>Work Order:</strong> {selectedJob.wo}</p>
                <p><strong>Company:</strong> {selectedJob.companyUse || "N/A"}</p>
                <p><strong>Client Work Order #:</strong> {selectedJob.clientWorkOrder || ''}</p>
                <p><strong>Address:</strong> {selectedJob.address}</p>
                <p><strong>City:</strong> {selectedJob.city}</p>
                <p><strong>State:</strong> {selectedJob.state}</p>
                <p><strong>Start Date:</strong> {selectedJob.startDate}</p>
                <p><strong>End Date:</strong> {selectedJob.endDate}</p>
                <p><strong>Start Time:</strong> {selectedJob.startTime || "N/A"}</p>
<p><strong>End Time:</strong> {selectedJob.endTime || "N/A"}</p>
                <button
                  type="button"
                  className="blue-button"
                  onClick={() => openScheduleRecommendations("edit", selectedJob)}
                  style={{ marginBottom: 10 }}
                >
                  Recommend Schedule
                </button>
                {recommendationTarget === "edit" && renderScheduleRecommendationPanel()}
                <p><strong>Service Type:</strong> {selectedJob.serviceType}</p>
                <p><strong>Scope:</strong> {selectedJob.scope}</p>
                <div style={{ margin: "10px 0" }}>
  <strong>Job Description:</strong>
  <div
    style={{
      marginTop: 8,
      padding: "12px 14px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      background: "#f9fafb",
      textAlign: "left",
    }}
  >
    {renderProposalJobDescription(selectedJob.jobDescription)}
  </div>
</div>
                <p><strong>Tech Assign:</strong> {Array.isArray(selectedJob.techAssign) ? selectedJob.techAssign.join(", ") : selectedJob.techAssign}</p>
                <p><strong>Contracted Job:</strong> {selectedJob.isContractJob ? "Yes" : "No"}</p>
                {selectedJob.isContractJob && (
                  <>
                    <p><strong>Billing Frequency:</strong> {selectedJob.contractBillingFrequency || "Monthly"}</p>
                    <p><strong>Contract Amount:</strong> ${Number(selectedJob.contractMonthlyAmount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </>
                )}
                <p><strong>Status:</strong> {selectedJob.status}</p>
                {selectedJob.isProject && (
  <p
    style={{
      background: "#ecfeff",
      border: "1px solid #06b6d4",
      padding: 8,
      borderRadius: 8,
      fontWeight: 800,
    }}
  >
    🏗️ Project Job — Day {selectedJob.projectDayNumber || 1} —{" "}
    {selectedJob.projectStatus || "Active"}
  </p>
)}
                <p><strong>Created On:</strong> {selectedJob.creationDate ? new Date(selectedJob.creationDate.seconds * 1000).toLocaleString() : 'N/A'}</p>
                <p>
  <strong>Created By:</strong>{" "}
  {selectedJob.createdBy
    ? `${selectedJob.createdBy.firstName} ${selectedJob.createdBy.lastName}`
    : "Unknown"}
</p>
                <h3>Notes</h3>
                <div className="notes-container">
                  {Array.isArray(selectedJob.notes) && selectedJob.notes.length > 0 ? (
                    <ul>
                      {selectedJob.notes.map((note, index) => (
                        <li key={index}>
                          <strong>{note.author} ({note.timestamp}):</strong> {note.text}
                         
                         
                          {note.files && note.files.map((file, fileIndex) => (
  <p key={fileIndex}>
    <a href={file.fileURL} target="_blank" rel="noopener noreferrer">
      {file.fileURL.match(/\.(jpeg|jpg|gif|png)$/i) ? (
        <img src={file.fileURL} alt={`Attachment ${fileIndex + 1}`} className="thumbnail" />
      ) : (
        `View Attachment ${fileIndex + 1}`
      )}
    </a>
  </p>
))}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No notes yet.</p>
                  )}
                </div>

                {canSeeSupervisorNotes && (
  <div
    style={{
      marginTop: 16,
      padding: 12,
      border: "2px solid #7c3aed",
      borderRadius: 10,
      background: "#f5f3ff",
    }}
  >
    <h3 style={{ marginTop: 0 }}>Supervisor Notes</h3>

    {Array.isArray(selectedJob.supervisorNotes) &&
    selectedJob.supervisorNotes.length > 0 ? (
      <ul style={{ paddingLeft: 18 }}>
        {selectedJob.supervisorNotes.map((note) => (
          <li
            key={note.id}
            style={{
              marginBottom: 10,
              padding: 8,
              background: "white",
              border: "1px solid #c4b5fd",
              borderRadius: 8,
            }}
          >
            <div>
  <div style={{ whiteSpace: "pre-wrap" }}>{note.text}</div>

  <div style={{ fontSize: 12, color: "#4b5563", marginTop: 4 }}>
    Added by {note.author || "User"}
    {note.authorRole ? ` (${note.authorRole})` : ""}{" "}
    {note.createdAt
      ? `on ${new Date(note.createdAt).toLocaleString()}`
      : ""}
  </div>
</div>
          </li>
        ))}
      </ul>
    ) : (
      <p>No supervisor notes yet.</p>
    )}
  </div>
)}
                {selectedJob.fileURLs && selectedJob.fileURLs.length > 0 && (
                  <div>
                    <h3>Uploaded Files</h3>
                    <div className="uploaded-files">
                      {selectedJob.fileURLs.map((fileURL, index) => (
                        <div key={index}>
                          <a href={fileURL} target="_blank" rel="noopener noreferrer">
                            {fileURL.match(/\.(jpeg|jpg|gif|png)$/) ? (
                              <img src={fileURL} alt={`File ${index + 1}`} className="thumbnail" />
                            ) : (
                              `View File ${index + 1}`
                            )}
                          </a>
                          <p>{selectedJob.fileNames && selectedJob.fileNames[index]}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedJob.newFiles && selectedJob.newFiles.length > 0 && (
                  <div>
                    <h3>New Attachments</h3>
                    <div className="new-attachments">
                      {selectedJob.newFiles.map((fileURL, index) => (
                        <img key={index} src={fileURL} alt={`New Attachment ${index + 1}`} className="thumbnail" />
                      ))}
                    </div>
                  </div>
                )}

                <div className="modal-buttons">
                  
                {["Admin", "Manager", "Assistant"].includes(userRole) && (
                   <button 
                   className="grey-button"
                   onClick={() => {
                    setEditedJob({
                      ...selectedJob,
                      techAssign: Array.isArray(selectedJob.techAssign)
                        ? selectedJob.techAssign
                        : [selectedJob.techAssign || ""],
                      startTimeMode: selectedJob.startTime?.includes(":") ? "custom" : "preset",
                      companyUseMode:
                        ["JMC", "DCS", "OGR", "MHI"].includes(selectedJob.companyUse)
                          ? selectedJob.companyUse
                          : (selectedJob.companyUse ? "Other" : ""),
                      recurrence: selectedJob.recurrence || {
                        type: "none",
                        interval: 1,
                        daysOfWeek: [],
                        customDates: [],
                        timesPerWeek: null,
                        endsOn: ""
                      }
                    });
 
                     setIsEditing(true);
                   }}
                 >
                   Edit
                 </button>
                 
                  )}
                  <button className="blue-button" onClick={() => setShowNotePopup(true)}>Add Note</button>
                  {canSeeSupervisorNotes && (
  <button className="grey-button" onClick={addSupervisorNote}>
    Add Supervisor Note
  </button>
)}
                  <button className="green-button" onClick={() => setShowVerificationPopup(true)}>
  Mark Job as Completed
</button>

                  <button className="grey-button" onClick={() => setShowHistoryPopup(true)}>
  History
</button>

                  </div>
                  <div className="modal-buttons">       
                  <button
  className="green-button"
  onClick={() => {
    setTotalInput("");
    setShowTotalPopup(true);
  }}
>
  Add / Change Total
</button>


       
                  {["Admin", "Manager", "Assistant"].includes(userRole) && (
                    <button className="black-button" onClick={() => setShowDeletePopup(true)}>Delete</button>
                  )}
                  <button
  className="blue-button"
  onClick={() => {
    if (!selectedJob) return;

    const stemRoot = getWOStemRoot(selectedJob.wo);

    setSelectedWOStem(stemRoot);
    setJob((prev) => ({
      ...prev,
      siteName: selectedJob.siteName || "",
      management: selectedJob.management || "",
      address: selectedJob.address || "",
      city: selectedJob.city || "",
      state: selectedJob.state || "",
      zip: selectedJob.zip || "",
      email: selectedJob.email || "",
      phone: selectedJob.phone || "",
      serviceType: selectedJob.serviceType || "",
      scope: selectedJob.scope || "",
      jobDescription: selectedJob.jobDescription || "",
      referral: selectedJob.referral || "",
      companyUse: selectedJob.companyUse || "",
      clientWorkOrder: selectedJob.clientWorkOrder || "",
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
      techAssign: [],
      status: "Active",
      jobPlan: selectedJob.jobPlan || {
        estimatedDays: "",
        estimatedLabor: "",
        materials: [],
      },
    }));
    setSelectedJob(null);
    setIsScheduling(true);
  }}
>
  Extend From This WO
</button>

                  <button
  className="blue-button"
  onClick={async () => {
    if (!selectedJob) return;

    const newWO = await getNextWoNumber();

    const duplicatedJob = {
      ...selectedJob,
    
      wo: newWO,
    
      // scheduling reset
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
    
      // keep planning + materials
      jobPlan: selectedJob.jobPlan || {},
    
      // reset execution state
      status: "Active",
      creationDate: new Date(),
      notes: [],
      completedDates: [],
      fileURLs: [],
      fileNames: [],
    };
    
    delete duplicatedJob.id;
    

    await addDoc(collection(db, "jobs"), duplicatedJob);

    alert(`Job duplicated. New WO #${newWO}`);
    closeModal();
    fetchJobs();
  }}
>
  📄 Duplicate Job
</button>

                  <button className="red-button" onClick={closeModal}>Close</button>
                </div>
              </>
            )}
          </div>
        </>,
        document.body
      )}

{showMaterialsPopup && createPortal(
  <>
    <div
      onClick={() => setShowMaterialsPopup(false)}
      style={{
        position: "fixed", inset: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.55)", zIndex: 1999998,
      }}
    />
    <div
      style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", margin: 0,
        width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
        background: "#fff", padding: 20, borderRadius: 8,
        boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
        boxSizing: "border-box", zIndex: 1999999,
      }}
    >
      <h3>Materials</h3>

      {materialsDraft.map((m, i) => (
        <div key={i} style={{ marginBottom: 10 }}>
          <input
            placeholder="Material name"
            value={m.name}
            onChange={(e) => {
              const copy = [...materialsDraft];
              copy[i].name = e.target.value;
              setMaterialsDraft(copy);
            }}
          />

          <input
            type="number"
            min="1"
            value={m.qty}
            onChange={(e) => {
              const copy = [...materialsDraft];
              copy[i].qty = Number(e.target.value);
              setMaterialsDraft(copy);
            }}
          />

          <input
            placeholder="Unit (pcs, ft, bags)"
            value={m.unit}
            onChange={(e) => {
              const copy = [...materialsDraft];
              copy[i].unit = e.target.value;
              setMaterialsDraft(copy);
            }}
          />

          <input
            placeholder="Notes"
            value={m.notes}
            onChange={(e) => {
              const copy = [...materialsDraft];
              copy[i].notes = e.target.value;
              setMaterialsDraft(copy);
            }}
          />
        </div>
      ))}

      <button className="blue-button" onClick={addMaterial}>
        + Add Material
      </button>

      <div className="modal-buttons">
        <button className="green-button" onClick={saveMaterials}>
          Save
        </button>
        <button
          className="red-button"
          onClick={() => setShowMaterialsPopup(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  </>,
  document.body
)}

      {showNotePopup && createPortal(
        <>
          <div
            onClick={closeNotePopup}
            style={{
              position: "fixed", inset: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.55)", zIndex: 1999998,
            }}
          ></div>
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", margin: 0,
              width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
              background: "#fff", padding: 20, borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
              boxSizing: "border-box", zIndex: 1999999,
            }}
          >
            <h2>Add Note</h2>
            <textarea className="form-container" rows="4" placeholder="Enter your note here..." value={noteText} onChange={(e) => setNoteText(e.target.value)}></textarea>
            <input type="file" accept="image/*, .pdf" onChange={handleFileChange} multiple />
            <div className="modal-buttons">
              <button className="green-button" onClick={saveNote} disabled={uploading}>{uploading ? <LoadingSpinner label="Saving note..." /> : "Save"}</button>
              <button className="red-button" onClick={closeNotePopup}>Cancel</button>
            </div>
          </div>
        </>,
        document.body
      )}

      {showHistoryPopup && (
        <JobHistoryPopup
          job={selectedJob}
          jobs={jobs}
          onClose={() => setShowHistoryPopup(false)}
        />
      )}

      {showCompleteConfirmPopup && createPortal(
        <>
          <div
            onClick={() => setShowCompleteConfirmPopup(false)}
            style={{
              position: "fixed", inset: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.55)", zIndex: 1999998,
            }}
          ></div>
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", margin: 0,
              width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
              background: "#fff", padding: 20, borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
              boxSizing: "border-box", zIndex: 1999999,
            }}
          >
            <h2>Mark Job as Completed</h2>
            <p>Are you sure you want to mark this job as completed?</p>
            <div className="modal-buttons">
              <button
                className="green-button"
                onClick={() => {
                  setShowCompleteConfirmPopup(false);
                  markJobCompleted();
                }}
              >
                Yes
              </button>
              <button className="red-button" onClick={() => setShowCompleteConfirmPopup(false)}>
                No
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {showVerificationPopup && selectedJob && createPortal(
        <div
          style={{
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 1999998,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <JobCompletionInvoiceModal
            job={selectedJob}
            onClose={closeVerificationPopup}
            onCompleted={async () => {
              // The form already completed the job (canonical occurrence key
              // for recurring). Close out the source proposal for one-time jobs.
              completionSubmittedRef.current = true;
              if (!isRecurringJob(selectedJob)) {
                await markSourceProposalCompleted(selectedJob);
              }
            }}
          />
        </div>,
        document.body
      )}

{showTotalPopup && createPortal(
  <>
    <div
      onClick={() => setShowTotalPopup(false)}
      style={{
        position: "fixed", inset: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.55)", zIndex: 1999998,
      }}
    />
    <div
      style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", margin: 0,
        width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
        background: "#fff", padding: 20, borderRadius: 8,
        boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
        boxSizing: "border-box", zIndex: 1999999,
      }}
    >
      <h3>Set Job Total</h3>

      <label>
        Total Amount
        <div className="price-input">
          <span>$</span>
          <input
            type="number"
            value={totalInput}
            onChange={(e) => setTotalInput(e.target.value)}
          />
        </div>
      </label>

      <div className="modal-buttons">
        <button
          className="green-button"
          onClick={async () => {
            const parsedTotal = Number(totalInput);

            if (Number.isNaN(parsedTotal) || parsedTotal <= 0) {
              alert("Please enter a valid total amount.");
              return;
            }

            const jobKey = String(selectedJob.wo);

            await addJobTotal({
              jobId: jobKey,
              wo: jobKey,
              proposalId: selectedJob.proposalId || null,
              total: parsedTotal,
              source: "job",
            });

            setShowTotalPopup(false);
          }}
        >
          Save
        </button>

        <button
          className="red-button"
          onClick={() => setShowTotalPopup(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  </>,
  document.body
)}


      {showDeletePopup && createPortal(
        <>
          <div
            onClick={() => setShowDeletePopup(false)}
            style={{
              position: "fixed", inset: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.55)", zIndex: 1999998,
            }}
          ></div>
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", margin: 0,
              width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
              background: "#fff", padding: 20, borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
              boxSizing: "border-box", zIndex: 1999999,
            }}
          >
            <h3>Are you sure you want to delete this job?</h3>
            <div className="button-group">
              <button className="shortgreen-button" onClick={handleDeleteJob}>Yes</button>
              <button className="shortred-button" onClick={() => setShowDeletePopup(false)}>No</button>
            </div>
          </div>
        </>,
        document.body
      )}
      </>)}
    </div>
  );
};

export default Scheduling;